import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {

  Activity,

  AlertTriangle,

  ArrowRight,

  BarChart3,

  Bell,

  CalendarDays,

  CheckCircle2,

  ChevronDown,

  Clock3,

  Database,

  Eye,

  Filter,

  Gauge,

  Globe2,

  Landmark,

  Layers3,

  RefreshCw,

  Search,

  ShieldAlert,

  ShieldCheck,

  Sparkles,

  Target,

  TrendingDown,

  TrendingUp,

  X,

} from "lucide-react";

import { apiGetFundamentalDashboard } from "../api/api";



const CACHE_KEY = "fullmetrix_fundamental_dashboard_v12";

const TZ_KEY = "fullmetrix_fundamental_timezone_v6";

const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const REQUEST_TIMEOUT_MS = 30000;

const POLL_INTERVAL_MS = 120000;



const EMPTY = {

  updated_at: null,

  data_health: { coverage_score: 0, message: "Synchronisation en cours." },

  overview: {

    market_sentiment: "En préparation",

    strongest_currency: "—",

    strongest_score: 50,

    weakest_currency: "—",

    weakest_score: 50,

    major_event: "Aucun événement majeur",

    major_event_date: null,

    trade_permission: "caution",

    trade_permission_text: "Attendre davantage de données avant de conclure.",

  },

  quick_decision: {

    global_bias: "Analyse en préparation",

    best_assets: [],

    avoid_assets: [],

    next_risk: "Aucun risque majeur détecté",

    next_risk_date: null,

    volatility_window: "Aucune fenêtre critique",

    trade_permission: "caution",

    trade_permission_text: "Attendre davantage de données avant de conclure.",

    simple_rule: "Comparer une devise forte à une devise faible puis attendre la confirmation du prix.",

  },

  ai_insight: {

    title: "Lecture fondamentale en préparation",

    summary: "FullMetrix rassemble les données macro disponibles.",

    confidence: 0,

    reasons: [],

  },

  economic_events: [],

  currency_strength: [],

  market_bias: [],

  opportunities: [],

  pair_playbooks: [],

  macro_regimes: [],

  event_risk: {},

  alerts: [],

  central_banks: [],

  geopolitical: { risk_score: 0, risk_level: "Faible", summary: "" },

  positioning: { currencies: {} },

  global_rates: { yields: {} },

  methodology: { factors: [] },

  warnings: [],

};



const COMMON_TIME_ZONES = [

  "UTC",

  "Africa/Lome",

  "Africa/Accra",

  "Africa/Abidjan",

  "Africa/Lagos",

  "Europe/London",

  "Europe/Paris",

  "America/New_York",

  "America/Chicago",

  "America/Los_Angeles",

  "Asia/Dubai",

  "Asia/Tokyo",

  "Asia/Singapore",

  "Australia/Sydney",

];


const CURRENCY_META = {
  USD: { flag: "🇺🇸", name: "Dollar américain", region: "États-Unis", bank: "Federal Reserve", bankShort: "Fed" },
  EUR: { flag: "🇪🇺", name: "Euro", region: "Zone euro", bank: "Banque centrale européenne", bankShort: "BCE" },
  GBP: { flag: "🇬🇧", name: "Livre sterling", region: "Royaume-Uni", bank: "Bank of England", bankShort: "BoE" },
  JPY: { flag: "🇯🇵", name: "Yen japonais", region: "Japon", bank: "Bank of Japan", bankShort: "BoJ" },
  CHF: { flag: "🇨🇭", name: "Franc suisse", region: "Suisse", bank: "Banque nationale suisse", bankShort: "BNS" },
  CAD: { flag: "🇨🇦", name: "Dollar canadien", region: "Canada", bank: "Banque du Canada", bankShort: "BoC" },
  AUD: { flag: "🇦🇺", name: "Dollar australien", region: "Australie", bank: "Reserve Bank of Australia", bankShort: "RBA" },
  NZD: { flag: "🇳🇿", name: "Dollar néo-zélandais", region: "Nouvelle-Zélande", bank: "Reserve Bank of New Zealand", bankShort: "RBNZ" },
};

function currencyMeta(itemOrCode) {
  const code = typeof itemOrCode === "string" ? itemOrCode : itemOrCode?.currency;
  const fallback = CURRENCY_META[code] || { flag: "🌐", name: code || "Devise", region: "Marché FX", bank: "Banque centrale", bankShort: "BC" };
  if (!itemOrCode || typeof itemOrCode === "string") return fallback;
  return {
    ...fallback,
    flag: itemOrCode.flag || fallback.flag,
    name: itemOrCode.currency_name || fallback.name,
  };
}


function FlagIcon({ code, size = "inline", className = "" }) {
  const currency = String(code || "").toUpperCase();
  const common = { viewBox: "0 0 28 20", role: "img", "aria-label": `${currency || "FX"} flag`, focusable: "false" };

  let art = null;
  if (currency === "USD") {
    art = (
      <svg {...common}>
        <rect width="28" height="20" fill="#fff" />
        {[0, 3.08, 6.16, 9.24, 12.32, 15.4, 18.48].map((y) => <rect key={y} y={y} width="28" height="1.54" fill="#b22234" />)}
        <rect width="12.2" height="10.77" fill="#3c3b6e" />
        {[2,5,8,11].flatMap((x) => [2,5,8].map((y) => <circle key={`${x}-${y}`} cx={x} cy={y} r=".55" fill="#fff" />))}
      </svg>
    );
  } else if (currency === "EUR") {
    const stars = Array.from({ length: 12 }, (_, i) => {
      const a = (i * 30 - 90) * Math.PI / 180;
      return <circle key={i} cx={14 + Math.cos(a) * 6.1} cy={10 + Math.sin(a) * 6.1} r=".7" fill="#ffcc00" />;
    });
    art = <svg {...common}><rect width="28" height="20" fill="#003399" />{stars}</svg>;
  } else if (currency === "GBP") {
    art = (
      <svg {...common}>
        <rect width="28" height="20" fill="#012169" />
        <path d="M0 0L28 20M28 0L0 20" stroke="#fff" strokeWidth="4.2" />
        <path d="M0 0L28 20M28 0L0 20" stroke="#C8102E" strokeWidth="1.7" />
        <path d="M14 0V20M0 10H28" stroke="#fff" strokeWidth="6" />
        <path d="M14 0V20M0 10H28" stroke="#C8102E" strokeWidth="3.2" />
      </svg>
    );
  } else if (currency === "JPY") {
    art = <svg {...common}><rect width="28" height="20" fill="#fff" /><circle cx="14" cy="10" r="5.1" fill="#bc002d" /></svg>;
  } else if (currency === "CHF") {
    art = (
      <svg {...common}>
        <rect width="28" height="20" fill="#d52b1e" />
        <rect x="11.5" y="4" width="5" height="12" fill="#fff" />
        <rect x="8" y="7.5" width="12" height="5" fill="#fff" />
      </svg>
    );
  } else if (currency === "CAD") {
    art = (
      <svg {...common}>
        <rect width="28" height="20" fill="#fff" />
        <rect width="6" height="20" fill="#d80621" /><rect x="22" width="6" height="20" fill="#d80621" />
        <path d="M14 3.1l1.2 3 2-.9-.65 2.25 2.15.7-2.35 2.1.85 1.55-2.45-.35-.75 4.1-.75-4.1-2.45.35.85-1.55-2.35-2.1 2.15-.7-.65-2.25 2 .9z" fill="#d80621" />
      </svg>
    );
  } else if (currency === "AUD" || currency === "NZD") {
    const nz = currency === "NZD";
    art = (
      <svg {...common}>
        <rect width="28" height="20" fill={nz ? "#00247d" : "#00008b"} />
        <g transform="scale(.47)">
          <rect width="28" height="20" fill="#012169" />
          <path d="M0 0L28 20M28 0L0 20" stroke="#fff" strokeWidth="4.2" />
          <path d="M0 0L28 20M28 0L0 20" stroke="#C8102E" strokeWidth="1.7" />
          <path d="M14 0V20M0 10H28" stroke="#fff" strokeWidth="6" />
          <path d="M14 0V20M0 10H28" stroke="#C8102E" strokeWidth="3.2" />
        </g>
        {nz ? (
          <g fill="#cc142b" stroke="#fff" strokeWidth=".55">
            <circle cx="20.5" cy="5.4" r="1.1"/><circle cx="23.5" cy="9.3" r="1.1"/><circle cx="19.2" cy="12.2" r="1.1"/><circle cx="23" cy="15.4" r="1.1"/>
          </g>
        ) : (
          <g fill="#fff">
            <circle cx="20.2" cy="5.1" r="1.05"/><circle cx="23.2" cy="9.3" r=".85"/><circle cx="19.3" cy="12.8" r="1.1"/><circle cx="23.4" cy="16" r=".85"/><circle cx="15.9" cy="15.8" r=".72"/>
          </g>
        )}
      </svg>
    );
  } else {
    art = <svg {...common}><rect width="28" height="20" fill="#eef2ff" /><circle cx="14" cy="10" r="5.5" fill="none" stroke="#7c3aed" strokeWidth="1.5" /><path d="M8.5 10h11M14 4.5c2 2.1 2 8.9 0 11M14 4.5c-2 2.1-2 8.9 0 11" stroke="#7c3aed" strokeWidth="1" fill="none" /></svg>;
  }

  return <span className={`fmx-flag-icon is-${size} ${className}`.trim()}>{art}</span>;
}

function currencyStrengthLabel(score) {
  const value = Number(score || 0);
  if (value >= 67) return "Très soutenue";
  if (value >= 57) return "Soutenue";
  if (value <= 33) return "Très sous pression";
  if (value <= 43) return "Sous pression";
  return "Équilibrée";
}



function clamp(value, min = 0, max = 100) {

  const number = Number(value);

  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : min;

}



function isValidTimeZone(value) {

  if (!value) return false;

  try {

    new Intl.DateTimeFormat("fr-FR", { timeZone: value }).format(new Date());

    return true;

  } catch {

    return false;

  }

}



function detectTimeZone() {

  try {

    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  } catch {

    return "UTC";

  }

}



function getInitialTimeZone() {

  if (typeof window !== "undefined") {

    try {

      const stored = window.localStorage.getItem(TZ_KEY);

      if (isValidTimeZone(stored)) return stored;

    } catch {

      // stockage facultatif

    }

  }

  const detected = detectTimeZone();

  return isValidTimeZone(detected) ? detected : "UTC";

}



function formatTime(value, timeZone) {

  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("fr-FR", {

    timeZone,

    hour: "2-digit",

    minute: "2-digit",

    hourCycle: "h23",

  }).format(date);

}



function formatDateTime(value, timeZone) {

  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("fr-FR", {

    timeZone,

    weekday: "short",

    day: "2-digit",

    month: "short",

    hour: "2-digit",

    minute: "2-digit",

    hourCycle: "h23",

  }).format(date);

}



function formatAge(value) {

  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  const minutes = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));

  if (minutes < 1) return "à l’instant";

  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);

  if (hours < 24) return `${hours} h`;

  return `${Math.floor(hours / 24)} j`;

}



function impactLabel(value) {

  const key = String(value || "").toLowerCase();

  if (["high", "fort", "critical", "danger", "3", "red"].includes(key)) return "Fort";

  if (["medium", "moyen", "warning", "2", "orange", "yellow"].includes(key)) return "Moyen";

  return "Faible";

}



function directionMeta(value) {

  const key = String(value || "").toLowerCase();

  if (["up", "bullish", "positive", "long", "haussier"].includes(key)) {

    return { key: "up", label: "Haussier", icon: TrendingUp };

  }

  if (["down", "bearish", "negative", "short", "baissier"].includes(key)) {

    return { key: "down", label: "Baissier", icon: TrendingDown };

  }

  return { key: "neutral", label: "Neutre", icon: Activity };

}



function permissionMeta(value) {

  const key = String(value || "caution").toLowerCase();

  if (key === "blocked") return { key: "blocked", label: "News proche", icon: ShieldAlert };

  if (key === "open") return { key: "open", label: "Contexte calme", icon: ShieldCheck };

  return { key: "caution", label: "Prudence", icon: AlertTriangle };

}



function scoreTone(score) {

  const value = Number(score || 0);

  if (value >= 57) return "up";

  if (value <= 43) return "down";

  return "neutral";

}



function readCache() {

  if (typeof window === "undefined") return { data: EMPTY, valid: false };

  try {

    const raw = window.localStorage.getItem(CACHE_KEY);

    if (!raw) return { data: EMPTY, valid: false };

    const parsed = JSON.parse(raw);

    if (!parsed?.savedAt || !parsed?.data) return { data: EMPTY, valid: false };

    if (Date.now() - Number(parsed.savedAt) > CACHE_MAX_AGE_MS) return { data: EMPTY, valid: false };

    return { data: { ...EMPTY, ...parsed.data }, valid: true };

  } catch {

    return { data: EMPTY, valid: false };

  }

}



function writeCache(data) {

  if (typeof window === "undefined") return;

  try {

    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ savedAt: Date.now(), data }));

  } catch {

    // stockage facultatif

  }

}



function withTimeout(promise) {

  let timer;

  const timeout = new Promise((_, reject) => {

    timer = window.setTimeout(() => reject(new Error("Le serveur met trop de temps à répondre.")), REQUEST_TIMEOUT_MS);

  });

  return Promise.race([promise, timeout]).finally(() => window.clearTimeout(timer));

}



function SectionHead({ icon: Icon, title, subtitle, action }) {

  return (

    <div className="fmx-section-head">

      <div className="fmx-section-title">

        <span><Icon /></span>

        <div>

          <h2>{title}</h2>

          {subtitle ? <p>{subtitle}</p> : null}

        </div>

      </div>

      {action}

    </div>

  );

}



function EmptyState({ title, text }) {

  return (

    <div className="fmx-empty">

      <Database />

      <strong>{title}</strong>

      <span>{text}</span>

    </div>

  );

}



function signedNumber(value, digits = 1) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return `${number > 0 ? "+" : ""}${number.toFixed(digits)}`;
}

function signalTypeLabel(value) {
  const key = String(value || "").toLowerCase();
  if (key === "surprise") return "Réel vs prévision";
  if (key === "actual_vs_previous") return "Réel vs précédent";
  if (key === "expectation") return "Anticipation";
  return "Facteur macro";
}

function eventStateMeta(event) {
  const state = String(event?.data_state || event?.data_explanation?.state || "").toLowerCase();
  if (state === "published_positive") return { key: "up", label: "Surprise favorable", icon: TrendingUp };
  if (state === "published_negative") return { key: "down", label: "Surprise défavorable", icon: TrendingDown };
  if (state === "published_neutral") return { key: "neutral", label: "Résultat neutre", icon: Activity };
  return { key: "pending", label: "Publication à venir", icon: Clock3 };
}

function tradeStatusLabel(value) {
  const key = String(value || "").toLowerCase();
  if (key === "blocked") return "Fenêtre sensible";
  if (key === "wait_confirmation") return "Attendre confirmation";
  if (key === "prepare") return "Préparer, ne pas anticiper";
  if (key === "caution") return "Prudence";
  if (key === "closed") return "Publication passée";
  return "Surveillance";
}

function InsightBlock({ icon: Icon = Sparkles, eyebrow, title, text, tone = "neutral", children }) {
  return (
    <section className={`fmx-insight-block is-${tone}`}>
      <div className="fmx-insight-icon"><Icon /></div>
      <div className="fmx-insight-content">
        {eyebrow ? <span className="fmx-insight-eyebrow">{eyebrow}</span> : null}
        {title ? <h4>{title}</h4> : null}
        {text ? <p>{text}</p> : null}
        {children}
      </div>
    </section>
  );
}

function MetricCard({ label, value, detail, tone = "neutral" }) {
  return (
    <article className={`fmx-modal-metric is-${tone}`}>
      <small>{label}</small>
      <strong>{value ?? "—"}</strong>
      {detail ? <span>{detail}</span> : null}
    </article>
  );
}

function Modal({ open, onClose, title, subtitle, eyebrow = "Analyse détaillée", children }) {
  useEffect(() => {
    if (!open) return undefined;
    const old = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = old;
      window.removeEventListener("keydown", handleKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fmx-modal-backdrop" onMouseDown={onClose}>
      <section className="fmx-modal" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(event) => event.stopPropagation()}>
        <header className="fmx-modal-header">
          <div className="fmx-modal-heading">
            <span className="fmx-modal-eyebrow"><Sparkles /> {eyebrow}</span>
            <h3>{title}</h3>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <button type="button" className="fmx-modal-close" onClick={onClose} aria-label="Fermer"><X /></button>
        </header>
        <div className="fmx-modal-body">{children}</div>
      </section>
    </div>
  );
}


function DashboardView({ data, timeZone, onCurrency, onMarket, onEvent }) {

  const currencies = data.currency_strength || [];

  const markets = data.market_bias || [];

  const events = data.economic_events || [];

  const permission = permissionMeta(data.quick_decision?.trade_permission || data.overview?.trade_permission);

  const PermissionIcon = permission.icon;

  const strongest = currencies.find((item) => item.currency === data.overview?.strongest_currency) || currencies[0];

  const weakest = currencies.find((item) => item.currency === data.overview?.weakest_currency) || currencies[currencies.length - 1];

  const focus = (data.opportunities || [])[0] || markets.find((item) => String(item.direction || "").toLowerCase() !== "neutral") || null;

  const nextEvent = events.find((item) => impactLabel(item.impact) === "Fort") || events[0] || null;
  const strongestMeta = currencyMeta(strongest);
  const weakestMeta = currencyMeta(weakest);
  const nextEventMeta = currencyMeta(nextEvent?.currency);



  return (

    <>

      <section className="fmx-summary-grid">

        <article className={`fmx-kpi-card is-${permission.key}`}>

          <div className="fmx-kpi-icon"><PermissionIcon /></div>

          <div>

            <small>Timing marché</small>

            <strong>{permission.label}</strong>

            <p>{data.quick_decision?.trade_permission_text || data.overview?.trade_permission_text}</p>

          </div>

        </article>

        <article className="fmx-kpi-card">

          <div className="fmx-kpi-icon is-purple"><TrendingUp /></div>

          <div>

            <small>Force relative</small>

            <strong className="fmx-kpi-pairing"><span><FlagIcon code={strongest?.currency} />{strongest?.currency || "—"} <em>{Math.round(strongest?.score || 0)}</em></span><b>vs</b><span><FlagIcon code={weakest?.currency} />{weakest?.currency || "—"} <em>{Math.round(weakest?.score || 0)}</em></span></strong>

            <p>{strongest && weakest ? `${strongest.currency} est actuellement mieux soutenue que ${weakest.currency}. Ouvre une devise pour voir les facteurs qui créent cet écart.` : "Aucun écart de force suffisamment net n’est confirmé pour le moment."}</p>

          </div>

        </article>

        <article className="fmx-kpi-card">

          <div className="fmx-kpi-icon is-blue"><Target /></div>

          <div>

            <small>Paire à étudier</small>

            <strong>{focus?.symbol || "Aucune priorité"}</strong>

            <p>{focus?.reason || focus?.thesis || "Attendre un différentiel macro plus propre."}</p>

          </div>

        </article>

        <article className="fmx-kpi-card">

          <div className="fmx-kpi-icon is-amber"><Bell /></div>

          <div>

            <small>Prochain risque</small>

            <strong>{nextEvent?.event || data.quick_decision?.next_risk || "Aucun risque majeur"}</strong>

            <p>{nextEvent ? <><span className="fmx-inline-currency"><FlagIcon code={nextEvent.currency} />{nextEvent.currency}</span> · {formatDateTime(nextEvent.datetime, timeZone)}</> : data.quick_decision?.volatility_window}</p>

          </div>

        </article>

      </section>



      <section className="fmx-main-grid">

        <div className="fmx-card fmx-daily-brief">

          <SectionHead

            icon={Sparkles}

            title="Brief fondamental"

            subtitle="La lecture prioritaire du contexte actuel."

            action={<span className="fmx-confidence">Confiance {Math.round(data.ai_insight?.confidence || 0)}%</span>}

          />

          <div className="fmx-brief-body">

            <div className="fmx-brief-primary">

              <strong>{data.ai_insight?.title || "Synthèse en préparation"}</strong>

              <p>{data.ai_insight?.summary || data.quick_decision?.global_bias}</p>

              <div className="fmx-rule-box">

                <span>Règle simple</span>

                <strong>{data.quick_decision?.simple_rule}</strong>

              </div>

            </div>

            <div className="fmx-reasons">

              {(data.ai_insight?.reasons || []).slice(0, 4).map((reason, index) => (

                <div key={`${reason}-${index}`}><CheckCircle2 /><span>{reason}</span></div>

              ))}

              {!data.ai_insight?.reasons?.length ? <p>Aucune raison détaillée disponible pour le moment.</p> : null}

            </div>

          </div>

        </div>



        <div className="fmx-card fmx-focus-panel">

          <SectionHead icon={Target} title="Focus marché" subtitle="La paire avec l’écart fondamental le plus exploitable actuellement." />

          {focus ? (

            <button type="button" className="fmx-focus-card" onClick={() => onMarket(focus)}>

              <div className="fmx-focus-main">

                <small>Paire</small>

                <div className="fmx-focus-pair-pill">
                  <span className="fmx-focus-flag"><FlagIcon code={focus.base || String(focus.symbol || "").split("/")[0]} size="sm" /></span>
                  <span className="fmx-focus-code">{focus.base || String(focus.symbol || "").split("/")[0] || "—"}</span>
                  <span className="fmx-focus-separator">/</span>
                  <span className="fmx-focus-code">{focus.quote || String(focus.symbol || "").split("/")[1] || "—"}</span>
                  <span className="fmx-focus-flag"><FlagIcon code={focus.quote || String(focus.symbol || "").split("/")[1]} size="sm" /></span>
                </div>

              </div>

              <div className="fmx-focus-meta">
                <div className="fmx-focus-stat">

                  <small>Biais</small>

                  <strong>{focus.bias || directionMeta(focus.direction).label}</strong>

                </div>

                <div className="fmx-focus-stat">

                  <small>Confiance</small>

                  <strong>{Math.round(focus.confidence || 0)}%</strong>

                </div>

                <span className="fmx-focus-arrow"><ArrowRight /></span>
              </div>

            </button>

          ) : (

            <EmptyState title="Pas encore de paire claire" text="Le moteur ne force pas une idée lorsqu'il n'y a pas assez d'écart." />

          )}

        </div>

      </section>



      <section className="fmx-card fmx-strength-section">

        <SectionHead
          icon={BarChart3}
          title="Force relative des devises"
          subtitle="Classement fondamental des huit devises majeures. 50 = équilibre ; au-dessus, soutien relatif ; en dessous, pression relative."
          action={currencies.length ? (
            <div className="fmx-strength-legend">
              <span className="is-up"><i /> Soutien</span>
              <span className="is-neutral"><i /> Équilibre</span>
              <span className="is-down"><i /> Pression</span>
            </div>
          ) : null}
        />

        {currencies.length ? (() => {
          const ranked = [...currencies].sort((a, b) => Number(b.score || 0) - Number(a.score || 0));
          const strongestItem = ranked[0];
          const weakestItem = ranked[ranked.length - 1];
          const strongestMeta = currencyMeta(strongestItem);
          const weakestMeta = currencyMeta(weakestItem);
          const spread = Number(strongestItem?.score || 0) - Number(weakestItem?.score || 0);

          return <>
            <div className="fmx-strength-overview">
              <div className="fmx-strength-overview-copy">
                <span className="fmx-strength-kicker">Lecture du différentiel</span>
                <strong>
                  <span><FlagIcon code={strongestItem?.currency} /> {strongestItem?.currency}</span>
                  <small>contre</small>
                  <span><FlagIcon code={weakestItem?.currency} /> {weakestItem?.currency}</span>
                </strong>
                <p>
                  L'écart actuel est de <b>{spread.toFixed(1)} points</b>. Ce différentiel aide à repérer les oppositions fondamentales les plus nettes, sans remplacer la confirmation du prix.
                </p>
              </div>
              <div className="fmx-strength-spread">
                <small>Écart max</small>
                <strong>{spread.toFixed(1)}</strong>
                <span>points</span>
              </div>
            </div>

            <div className="fmx-currency-strip">
              {ranked.slice(0, 8).map((item, index) => {
                const meta = currencyMeta(item);
                const tone = scoreTone(item.score);
                return (
                  <button
                    type="button"
                    className={`fmx-currency-row is-${tone}`}
                    key={item.currency || index}
                    onClick={() => onCurrency(item)}
                    aria-label={`Ouvrir l'analyse de ${item.currency}`}
                  >
                    <div className="fmx-currency-identity">
                      <span className="fmx-currency-flag" aria-hidden="true"><FlagIcon code={item.currency} size="card" /></span>
                      <div>
                        <span className="fmx-currency-overline">#{index + 1} · {meta.bankShort}</span>
                        <strong>{item.currency}</strong>
                        <small>{meta.name}</small>
                      </div>
                    </div>

                    <div className="fmx-currency-scoreline">
                      <span>{currencyStrengthLabel(item.score)}</span>
                      <strong className={`is-${tone}`}>{Number(item.score || 0).toFixed(1)}<small>/100</small></strong>
                    </div>

                    <div className="fmx-strength-track" aria-hidden="true">
                      <i style={{ width: `${clamp(item.score || 0)}%` }} />
                      <b />
                    </div>

                    <div className="fmx-currency-foot">
                      <span>{item.score_status || "—"}</span>
                      <span>{Math.round(item.score_confidence || 0)}% confiance</span>
                      <ArrowRight />
                    </div>
                  </button>
                );
              })}
            </div>
          </>;
        })() : <EmptyState title="Scores indisponibles" text="Les forces monétaires apparaîtront ici dès que les données seront prêtes." />}

      </section>



      <section className="fmx-lower-grid">

        <div className="fmx-card">

          <SectionHead icon={CalendarDays} title="Calendrier à surveiller" subtitle="Les prochaines publications importantes." />

          {events.length ? (

            <div className="fmx-event-mini-list">

              {events.slice(0, 10).map((event) => {
                const eventMeta = currencyMeta(event.currency);
                return (
                  <button type="button" key={event.id} onClick={() => onEvent(event)}>
                    <span className="fmx-event-time"><strong>{formatTime(event.datetime, timeZone)}</strong><small><FlagIcon code={event.currency} />{event.currency}</small></span>
                    <span className="fmx-event-copy"><strong>{event.event}</strong><small>{event.category || "Macroéconomie"}</small></span>
                    <span className={`fmx-impact is-${impactLabel(event.impact).toLowerCase()}`}>{impactLabel(event.impact)}</span>
                    <ArrowRight />
                  </button>
                );
              })}

            </div>

          ) : <EmptyState title="Calendrier vide" text="Aucun événement économique disponible." />}

        </div>


      </section>

    </>

  );

}



function PairsView({ data, onMarket }) {

  const rows = data.market_bias || [];

  return (

    <section className="fmx-card">

      <SectionHead icon={Target} title="Laboratoire des paires" subtitle="Compare les différentiels fondamentaux avant de construire un scénario." />

      {rows.length ? (

        <div className="fmx-table-wrap">

          <div className="fmx-pair-table-head">

            <span>Paire</span><span>Biais</span><span>Écart</span><span>Confiance</span><span>Lecture</span><span></span>

          </div>

          {rows.slice(0, 20).map((item) => {

            const meta = directionMeta(item.direction);
            const baseMeta = currencyMeta(item.base);
            const quoteMeta = currencyMeta(item.quote);

            const Icon = meta.icon;

            return (

              <button type="button" className="fmx-pair-table-row" key={item.symbol} onClick={() => onMarket(item)}>

                <span className="fmx-pair-identity"><strong><FlagIcon code={item.base} size="sm" />{item.symbol}<FlagIcon code={item.quote} size="sm" /></strong><small>{baseMeta.name} vs {quoteMeta.name}</small></span>

                <span className={`fmx-bias is-${meta.key}`}><Icon /> {meta.label}</span>

                <span><strong>{Number(item.differential || 0) > 0 ? "+" : ""}{item.differential}</strong></span>

                <span><strong>{Math.round(item.confidence || 0)}%</strong><small>{item.data_confidence || 0}% données</small></span>

                <span className="fmx-thesis-cell">{item.thesis || "Lecture indisponible."}</span>

                <span><ArrowRight /></span>

              </button>

            );

          })}

        </div>

      ) : <EmptyState title="Aucune paire calculée" text="Le moteur attend des données suffisantes." />}

    </section>

  );

}



function CurrenciesView({ data, onCurrency }) {

  const currencies = [...(data.currency_strength || [])].sort((a, b) => Number(b.score || 0) - Number(a.score || 0));

  return (

    <section className="fmx-card fmx-currencies-lab">

      <SectionHead
        icon={BarChart3}
        title="Devises majeures"
        subtitle="Chaque carte combine score relatif, qualité des données et principaux éléments réellement observés."
      />

      {currencies.length ? (

        <div className="fmx-currency-grid">

          {currencies.map((item, index) => {
            const meta = currencyMeta(item);
            const tone = scoreTone(item.score);
            const leadDriver = item.driver_details?.[0]?.event || item.drivers?.[0] || "Aucun facteur dominant confirmé";
            return (
              <button
                type="button"
                className={`fmx-currency-card is-${tone}`}
                key={item.currency || index}
                onClick={() => onCurrency(item)}
              >
                <div className="fmx-currency-card-head">
                  <div className="fmx-currency-card-id">
                    <span className="fmx-currency-flag is-large"><FlagIcon code={item.currency} size="card" /></span>
                    <div>
                      <span>#{index + 1} · {meta.region}</span>
                      <strong>{item.currency}</strong>
                      <small>{meta.name}</small>
                    </div>
                  </div>
                  <div
                    className={`fmx-currency-score-orb is-${tone}`}
                    style={{ "--score-angle": `${clamp(item.score || 0) * 3.6}deg` }}
                  >
                    <div><strong>{Math.round(item.score || 0)}</strong><small>/100</small></div>
                  </div>
                </div>

                <div className="fmx-currency-card-reading">
                  <span>{currencyStrengthLabel(item.score)}</span>
                  <p>{item.plain_reading || item.summary || "Aucune lecture détaillée."}</p>
                </div>

                <div className="fmx-currency-driver">
                  <small>Facteur dominant</small>
                  <strong>{leadDriver}</strong>
                </div>

                <div className="fmx-currency-card-track">
                  <i style={{ width: `${clamp(item.score || 0)}%` }} />
                  <b />
                </div>

                <div className="fmx-currency-meta">
                  <span>{item.score_status || "—"}</span>
                  <span>{Math.round(item.score_confidence || 0)}% confiance</span>
                  <span>{item.evidence_count || 0} publiées</span>
                  <ArrowRight />
                </div>
              </button>
            );
          })}

        </div>

      ) : <EmptyState title="Aucune devise disponible" text="Les données de force relative apparaîtront ici." />}

    </section>

  );

}



function CalendarView({ data, timeZone, onEvent }) {

  const [query, setQuery] = useState("");

  const [impact, setImpact] = useState("all");

  const events = useMemo(() => {

    const q = query.trim().toLowerCase();

    return (data.economic_events || []).filter((event) => {

      const impactOk = impact === "all" || impactLabel(event.impact).toLowerCase() === impact;

      const text = `${event.event} ${event.currency} ${event.category || ""}`.toLowerCase();

      return impactOk && (!q || text.includes(q));

    });

  }, [data.economic_events, query, impact]);



  return (

    <section className="fmx-card">

      <SectionHead icon={CalendarDays} title="Calendrier économique" subtitle="Recherche, filtre par impact et lecture rapide des publications." />

      <div className="fmx-toolbar">

        <label className="fmx-search"><Search /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher un événement ou une devise..." /></label>

        <label className="fmx-select"><Filter /><select value={impact} onChange={(event) => setImpact(event.target.value)}><option value="all">Tous impacts</option><option value="fort">Fort</option><option value="moyen">Moyen</option><option value="faible">Faible</option></select><ChevronDown /></label>

      </div>

      {events.length ? (

        <div className="fmx-calendar-table">

          <div className="fmx-calendar-head"><span>Heure</span><span>Devise</span><span>Événement</span><span>Impact</span><span>Prévision</span><span>Réel</span><span></span></div>

          {events.slice(0, 30).map((event) => {
            const eventMeta = currencyMeta(event.currency);
            return (
              <button type="button" className="fmx-calendar-row" key={event.id} onClick={() => onEvent(event)}>
                <span><strong>{formatTime(event.datetime, timeZone)}</strong><small>{formatDateTime(event.datetime, timeZone).split(" ").slice(0, 3).join(" ")}</small></span>
                <span className="fmx-calendar-currency"><FlagIcon code={event.currency} size="sm" /><span><strong>{event.currency}</strong><small>{eventMeta.bankShort}</small></span></span>
                <span><strong>{event.event}</strong><small>{event.category || "Macroéconomie"}</small></span>
                <span><b className={`fmx-impact is-${impactLabel(event.impact).toLowerCase()}`}>{impactLabel(event.impact)}</b></span>
                <span>{event.forecast ?? "—"}</span>
                <span>{event.actual ?? "À venir"}</span>
                <span><ArrowRight /></span>
              </button>
            );
          })}

        </div>

      ) : <EmptyState title="Aucun événement trouvé" text="Change la recherche ou le niveau d’impact." />}

    </section>

  );

}



function MacroView({ data, timeZone }) {

  const banks = Array.isArray(data.central_banks) ? data.central_banks : [];
  const factors = Array.isArray(data.methodology?.factors) ? data.methodology.factors : [];
  const rates = data.global_rates?.yields && typeof data.global_rates.yields === "object"
    ? data.global_rates.yields
    : {};

  const eventLabel = (value, fallback = "Décision à confirmer") => {
    if (value === null || value === undefined || value === "") return fallback;
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (typeof value === "object") {
      return value.event || value.event_original || value.title || value.name || fallback;
    }
    return fallback;
  };

  const factorLabel = (value) => {
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (value && typeof value === "object") {
      return value.label || value.title || value.name || value.event || "Facteur macro";
    }
    return "Facteur macro";
  };

  const formatYield = (value) => {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return `${numeric.toFixed(2)}%`;
    if (typeof value === "string" && value.trim()) return value;
    return "—";
  };

  return (
    <div className="fmx-macro-grid">

      <section className="fmx-card fmx-macro-banks">
        <SectionHead
          icon={Landmark}
          title="Banques centrales"
          subtitle="Politique monétaire, biais actuel et prochaine décision identifiée par le moteur."
        />

        {banks.length ? (
          <div className="fmx-bank-list">
            {banks.map((bank, index) => {
              const meta = currencyMeta(bank.currency);
              const nextMeeting = bank.next_meeting || bank.next_decision || null;
              const policyKey =
                bank.policy_bias === "hawkish"
                  ? "hawkish"
                  : bank.policy_bias === "dovish"
                    ? "dovish"
                    : "neutral";

              return (
                <article key={bank.currency || bank.bank_short || index}>
                  <div className="fmx-bank-identity">
                    <span className="fmx-bank-flag"><FlagIcon code={bank.currency || bank.code} size="card" /></span>
                    <div>
                      <strong>{bank.bank_short || meta.bankShort || bank.currency || "BC"}</strong>
                      <small>{bank.bank || meta.bank || "Banque centrale"}</small>
                    </div>
                  </div>

                  <div className="fmx-bank-policy">
                    <span className={`fmx-policy-pill is-${policyKey}`}>
                      {bank.policy_label || "Neutre / en attente"}
                    </span>
                    <small>
                      {bank.policy_rate_reference !== null && bank.policy_rate_reference !== undefined
                        ? `Taux de référence : ${String(bank.policy_rate_reference)}`
                        : `Score politique : ${Math.round(Number(bank.policy_score || 50))}/100`}
                    </small>
                  </div>

                  <div className="fmx-bank-next">
                    <small>Prochaine décision</small>
                    <strong>{eventLabel(nextMeeting)}</strong>
                    <span>
                      {nextMeeting && typeof nextMeeting === "object" && nextMeeting.datetime
                        ? formatDateTime(nextMeeting.datetime, timeZone)
                        : "Date non disponible"}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <EmptyState
            title="Banques centrales indisponibles"
            text="Aucune matrice monétaire reçue du backend."
          />
        )}
      </section>

      <section className="fmx-card fmx-macro-rates">
        <SectionHead
          icon={Activity}
          title="Taux US & régime de risque"
          subtitle="La courbe des taux complète la lecture des anticipations de politique monétaire."
        />

        <div className="fmx-rate-grid">
          {Object.entries(rates).slice(0, 8).map(([key, value]) => (
            <article key={key}>
              <small>US Treasury</small>
              <strong>{formatYield(value)}</strong>
              <span>{String(key).toUpperCase()}</span>
            </article>
          ))}
        </div>

        {!Object.keys(rates).length ? (
          <p className="fmx-muted">Aucun taux disponible actuellement.</p>
        ) : null}

        <div className="fmx-curve-reading">
          <div>
            <small>Courbe 2Y–10Y</small>
            <strong>{data.global_rates?.curve_label || "Lecture indisponible"}</strong>
          </div>
          <span>
            {Number.isFinite(Number(data.global_rates?.curve_2s10s))
              ? `${Number(data.global_rates.curve_2s10s).toFixed(2)} pt`
              : "—"}
          </span>
        </div>

        <div className="fmx-geo-line">
          <Globe2 />
          <div>
            <small>Risque géopolitique</small>
            <strong>
              {data.geopolitical?.risk_level || "Faible"} ·{" "}
              {Math.round(Number(data.geopolitical?.risk_score || 0))}/100
            </strong>
            <p>
              {data.geopolitical?.summary ||
                "Le contexte géopolitique complète la lecture macro sans remplacer les données publiées."}
            </p>
          </div>
        </div>
      </section>

      <section className="fmx-card">
        <SectionHead
          icon={Layers3}
          title="Méthodologie"
          subtitle="Ce que le moteur combine avant de construire ses scores."
        />

        <div className="fmx-factor-list">
          {(factors.length
            ? factors
            : ["Surprises macro", "Anticipations", "Historique", "Géopolitique"]
          ).map((factor, index) => {
            const label = factorLabel(factor);
            return (
              <span key={`${label}-${index}`}>
                <CheckCircle2 /> {label}
              </span>
            );
          })}
        </div>
      </section>

      <section className="fmx-card">
        <SectionHead
          icon={Gauge}
          title="Qualité du moteur"
          subtitle="La couverture mesure les données disponibles, pas la certitude d'un trade."
        />

        <div className="fmx-quality-card">
          <strong>{Math.round(data.data_health?.coverage_score || 0)}%</strong>
          <span>Couverture actuelle</span>
          <div>
            <i style={{ width: `${clamp(data.data_health?.coverage_score || 0)}%` }} />
          </div>
          <p>{data.data_health?.message || "État des données disponibles."}</p>
        </div>
      </section>

    </div>
  );
}


function RegimesView({ data, onCurrency }) {
  const regimes = Array.isArray(data.macro_regimes) ? data.macro_regimes : [];
  const currencyMap = Object.fromEntries((data.currency_strength || []).map((item) => [item.currency, item]));
  const pillarLabels = { inflation: "Inflation", growth: "Croissance", labour: "Emploi", rates: "Taux", external: "Commerce", positioning: "COT" };
  return (
    <section className="fmx-card fmx-regimes-view">
      <SectionHead icon={Layers3} title="Régimes macro" subtitle="Le régime dominant de chaque devise avant de construire une paire." />
      {regimes.length ? <div className="fmx-regime-grid">
        {regimes.map((item) => {
          const meta = currencyMeta(item.currency);
          const source = currencyMap[item.currency] || { currency: item.currency };
          return <button key={item.currency} type="button" className={`fmx-regime-card is-${item.tone || 'neutral'}`} onClick={() => onCurrency(source)}>
            <div className="fmx-regime-top">
              <span className="fmx-regime-flag"><FlagIcon code={item.currency} size="card" /></span>
              <div><strong>{item.currency}</strong><small>{meta.name}</small></div>
              <b>{Math.round(Number(item.score || 50))}</b>
            </div>
            <div className="fmx-regime-name">{item.regime || "Régime mixte"}</div>
            <div className="fmx-regime-policy"><Landmark /><span>{item.policy_bias === 'hawkish' ? 'Politique restrictive' : item.policy_bias === 'dovish' ? 'Politique accommodante' : 'Politique neutre'}</span></div>
            <div className="fmx-pillar-mini-grid">
              {Object.entries(item.pillars || {}).map(([key, value]) => <div key={key} className="fmx-pillar-mini">
                <span><small>{pillarLabels[key] || key}</small><b>{Math.round(Number(value || 50))}</b></span>
                <div><i style={{ width: `${clamp(value || 50)}%` }} /></div>
              </div>)}
            </div>
            <div className="fmx-regime-footer"><span>Confiance {Math.round(Number(item.confidence || 0))}%</span><ArrowRight /></div>
          </button>;
        })}
      </div> : <EmptyState title="Régimes indisponibles" text="Le moteur attend davantage de données macro." />}
    </section>
  );
}

function CentralBanksView({ data, timeZone }) {
  const banks = Array.isArray(data.central_banks) ? data.central_banks : [];
  const eventText = (event) => !event ? "Aucune donnée" : typeof event === "string" ? event : event.event || event.event_original || "Publication macro";
  const valueText = (event) => !event || typeof event !== "object" ? "—" : `Réel ${event.actual ?? '—'} · Prévision ${event.forecast ?? '—'}`;
  return (
    <section className="fmx-card fmx-banks-view">
      <SectionHead icon={Landmark} title="Banques centrales" subtitle="Divergences de politique, taux de référence et prochains catalyseurs." />
      {banks.length ? <div className="fmx-bank-detail-grid">
        {banks.map((bank, index) => {
          const meta = currencyMeta(bank.currency);
          const next = bank.next_meeting;
          const policyKey = bank.policy_bias === 'hawkish' ? 'hawkish' : bank.policy_bias === 'dovish' ? 'dovish' : 'neutral';
          return <article className="fmx-bank-detail-card" key={bank.currency || index}>
            <header>
              <span className="fmx-bank-detail-flag"><FlagIcon code={bank.currency} size="card" /></span>
              <div><strong>{bank.bank_short || meta.bankShort}</strong><small>{bank.bank || meta.bank}</small></div>
              <span className={`fmx-policy-pill is-${policyKey}`}>{bank.policy_label || "Neutre"}</span>
            </header>
            <div className="fmx-bank-metrics">
              <div><small>Taux de référence</small><strong>{bank.policy_rate_reference ?? "—"}</strong></div>
              <div><small>Score politique</small><strong>{Math.round(Number(bank.policy_score || 50))}/100</strong></div>
              <div><small>Score devise</small><strong>{Math.round(Number(bank.currency_score || 50))}/100</strong></div>
            </div>
            <div className="fmx-bank-drivers">
              <div><span>Inflation</span><strong>{eventText(bank.latest_inflation)}</strong><small>{valueText(bank.latest_inflation)}</small></div>
              <div><span>Croissance</span><strong>{eventText(bank.latest_growth)}</strong><small>{valueText(bank.latest_growth)}</small></div>
              <div><span>Emploi</span><strong>{eventText(bank.latest_labour)}</strong><small>{valueText(bank.latest_labour)}</small></div>
            </div>
            <div className="fmx-bank-next-meeting"><CalendarDays /><div><small>Prochaine décision</small><strong>{eventText(next)}</strong><span>{next?.datetime ? formatDateTime(next.datetime, timeZone) : "Date non disponible"}</span></div></div>
          </article>;
        })}
      </div> : <EmptyState title="Banques centrales indisponibles" text="Aucune matrice monétaire disponible." />}
    </section>
  );
}

function PositioningView({ data }) {
  const positioning = data.positioning && typeof data.positioning === "object" ? data.positioning : {};
  const rows = Object.values(positioning.currencies || {}).filter((item) => item && typeof item === "object").sort((a, b) => Number(b.leveraged_net_pct_oi || 0) - Number(a.leveraged_net_pct_oi || 0));
  const biasLabel = (bias) => bias === 'bullish' ? 'Net acheteur' : bias === 'bearish' ? 'Net vendeur' : 'Neutre';
  return (
    <section className="fmx-card fmx-positioning-view">
      <SectionHead icon={BarChart3} title="Positionnement COT" subtitle="Leveraged Money hebdomadaire : contexte institutionnel, pas signal intraday." />
      <div className="fmx-positioning-note"><Database /><div><strong>{positioning.provider || "CFTC TFF Futures Only"}</strong><span>{positioning.methodology || "Lecture hebdomadaire du positionnement institutionnel."}</span></div><small>{positioning.report_date ? `Rapport ${formatDateTime(positioning.report_date, 'UTC')}` : "Rapport indisponible"}</small></div>
      {rows.length ? <div className="fmx-cot-grid">{rows.map((item) => {
        const pct = Number(item.leveraged_net_pct_oi || 0);
        const weekly = item.leveraged_weekly_change == null ? null : Number(item.leveraged_weekly_change);
        return <article className="fmx-cot-card" key={item.currency}>
          <header><span><FlagIcon code={item.currency} size="card" /></span><div><strong>{item.currency}</strong><small>{item.market || currencyMeta(item.currency).name}</small></div><b className={`is-${pct > 1 ? 'up' : pct < -1 ? 'down' : 'neutral'}`}>{biasLabel(item.bias)}</b></header>
          <div className="fmx-cot-main"><strong>{pct > 0 ? '+' : ''}{pct.toFixed(2)}%</strong><span>net Leveraged Money / open interest</span></div>
          <div className="fmx-cot-axis"><i className="is-zero" /><i className={pct >= 0 ? 'is-positive' : 'is-negative'} style={pct >= 0 ? { left: '50%', width: `${Math.min(50, Math.abs(pct))}%` } : { right: '50%', width: `${Math.min(50, Math.abs(pct))}%` }} /></div>
          <div className="fmx-cot-meta"><span><small>Variation hebdo</small><strong>{weekly != null && Number.isFinite(weekly) ? weekly.toLocaleString('fr-FR') : '—'}</strong></span><span><small>Asset managers</small><strong>{item.asset_manager_net_pct_oi != null ? `${Number(item.asset_manager_net_pct_oi).toFixed(2)}%` : '—'}</strong></span></div>
          {item.synthetic ? <p>{item.note}</p> : null}
        </article>;
      })}</div> : <EmptyState title="COT indisponible" text="Le dernier rapport CFTC n’est pas disponible actuellement." />}
    </section>
  );
}

function RisksView({ data, timeZone, onEvent }) {
  const risk = data.event_risk || {};
  const geo = data.geopolitical || {};
  const events = Array.isArray(risk.next_48h) ? risk.next_48h : [];
  const fullEvents = Array.isArray(data.economic_events) ? data.economic_events : [];
  const openRisk = (item) => onEvent(fullEvents.find((event) => event.id === item.id) || item);
  return (
    <div className="fmx-risk-layout">
      <section className="fmx-card">
        <SectionHead icon={ShieldAlert} title="Fenêtres de risque" subtitle="News et événements qui peuvent dégrader l’exécution dans les prochaines 48 heures." />
        <div className="fmx-risk-kpis"><article><small>6 heures</small><strong>{(risk.next_6h || []).length}</strong><span>publication(s)</span></article><article><small>24 heures</small><strong>{(risk.next_24h || []).length}</strong><span>publication(s)</span></article><article><small>News fortes 24h</small><strong>{risk.high_impact_24h || 0}</strong><span>risque volatilité</span></article></div>
        {events.length ? <div className="fmx-risk-events">{events.map((event, index) => <button type="button" key={event.id || index} onClick={() => openRisk(event)}><span className="fmx-risk-event-currency"><FlagIcon code={event.currency} size="sm" /><strong>{event.currency}</strong></span><span><strong>{event.event}</strong><small>{formatDateTime(event.datetime, timeZone)} · {event.countdown || ''}</small></span><b className={`fmx-impact is-${impactLabel(event.impact).toLowerCase()}`}>{impactLabel(event.impact)}</b><ArrowRight /></button>)}</div> : <EmptyState title="Aucune fenêtre critique" text="Aucune publication moyenne ou forte dans les prochaines 48 heures." />}
      </section>
      <section className="fmx-card fmx-geo-risk-card">
        <SectionHead icon={Globe2} title="Risque géopolitique" subtitle="Contexte de régime : il complète le macro sans le remplacer." />
        <div className="fmx-geo-score"><strong>{Math.round(Number(geo.risk_score || 0))}</strong><span>/100</span><b>{geo.risk_level || 'Faible'}</b></div>
        <p>{geo.summary || "Aucun contexte géopolitique significatif disponible."}</p>
        {(geo.headlines || []).length ? <div className="fmx-geo-headlines">{geo.headlines.slice(0, 6).map((item, index) => <article key={`${item.title}-${index}`}><strong>{item.title}</strong><small>{item.domain || 'Source externe'} · poids {item.risk_weight ?? '—'}</small></article>)}</div> : null}
      </section>
    </div>
  );
}

function PlaybooksView({ data, onMarket }) {
  const playbooks = Array.isArray(data.pair_playbooks) ? [...data.pair_playbooks] : [];
  playbooks.sort((a, b) => Number(b.fundamental_score || 0) - Number(a.fundamental_score || 0));
  const markets = Array.isArray(data.market_bias) ? data.market_bias : [];
  return (
    <section className="fmx-card fmx-playbooks-view">
      <SectionHead icon={Sparkles} title="Scénarios fondamentaux" subtitle="Confluences, contradictions, catalyseurs et invalidation pour les paires suivies." />
      {playbooks.length ? <div className="fmx-playbook-grid">{playbooks.map((item, index) => {
        const market = markets.find((row) => row.symbol === item.symbol) || item;
        const [base, quote] = String(item.symbol || '').split('/');
        return <button type="button" className="fmx-playbook-card" key={`${item.symbol}-${index}`} onClick={() => onMarket(market)}>
          <header><span className="fmx-playbook-pair"><FlagIcon code={base} size="sm" /><strong>{item.symbol}</strong><FlagIcon code={quote} size="sm" /></span><b>{item.fundamental_score || 0}/100</b></header>
          <div className="fmx-playbook-tags"><span>{item.setup || 'Attendre'}</span><span>{item.bias || directionMeta(item.direction).label}</span><span>Écart {Number(item.differential || 0) > 0 ? '+' : ''}{Number(item.differential || 0).toFixed(1)}</span></div>
          <p>{item.thesis || "Thèse fondamentale en préparation."}</p>
          <div className="fmx-playbook-columns"><div><small>Alignements</small>{(item.alignments || []).slice(0, 3).map((text, i) => <span key={i}><CheckCircle2 />{text}</span>)}{!item.alignments?.length ? <em>Aucun alignement fort</em> : null}</div><div><small>Conflits</small>{(item.conflicts || []).slice(0, 3).map((text, i) => <span key={i}><AlertTriangle />{text}</span>)}{!item.conflicts?.length ? <em>Pas de conflit majeur</em> : null}</div></div>
          <footer><span>Confiance {Math.round(Number(item.confidence || 0))}%</span><ArrowRight /></footer>
        </button>;
      })}</div> : <EmptyState title="Aucun scénario disponible" text="Le moteur attend un différentiel exploitable entre deux devises." />}
    </section>
  );
}


export default function FundamentalAnalysisPage() {

  const cached = useMemo(() => readCache(), []);

  const [data, setData] = useState(cached.data);

  const [loading, setLoading] = useState(!cached.valid);

  const [refreshing, setRefreshing] = useState(false);

  const [error, setError] = useState("");

  const [timeZone, setTimeZone] = useState(getInitialTimeZone);

  const [view, setView] = useState("overview");

  const [modal, setModal] = useState({ type: null, payload: null });

  const requestRef = useRef(false);

  const mountedRef = useRef(true);



  const closeModal = useCallback(() => setModal({ type: null, payload: null }), []);

  const loadDashboard = useCallback(async ({ silent = false } = {}) => {

    if (requestRef.current) return;

    requestRef.current = true;

    if (!silent) setRefreshing(true);

    if (!cached.valid && !silent) setLoading(true);

    try {

      const payload = await withTimeout(apiGetFundamentalDashboard({ refresh: !silent }));

      if (payload?.ok === false || payload?.error) throw new Error(payload?.error || "Impossible de charger les données fondamentales.");

      if (!mountedRef.current) return;

      const merged = { ...EMPTY, ...payload };

      setData(merged);

      writeCache(merged);

      setError("");

    } catch (err) {

      if (!mountedRef.current) return;

      setError(err?.message || "Impossible de joindre le moteur fondamental.");

    } finally {

      requestRef.current = false;

      if (mountedRef.current) {

        setLoading(false);

        setRefreshing(false);

      }

    }

  }, [cached.valid]);



  useEffect(() => {

    mountedRef.current = true;

    loadDashboard({ silent: true });

    const timer = window.setInterval(() => loadDashboard({ silent: true }), POLL_INTERVAL_MS);

    return () => {

      mountedRef.current = false;

      window.clearInterval(timer);

    };

  }, [loadDashboard]);



  useEffect(() => {

    try {

      window.localStorage.setItem(TZ_KEY, timeZone);

    } catch {

      // stockage facultatif

    }

  }, [timeZone]);



  const selectedMarket = modal.type === "market" ? modal.payload : null;

  const selectedCurrency = modal.type === "currency" ? modal.payload : null;

  const selectedEvent = modal.type === "event" ? modal.payload : null;

  const openMarket = useCallback((item) => {
    if (!item) return;
    const market = (data.market_bias || []).find((row) => row.symbol === item.symbol) || item;
    const playbook = (data.pair_playbooks || []).find((row) => row.symbol === market.symbol) || null;
    setModal({ type: "market", payload: { ...item, ...market, playbook } });
  }, [data.market_bias, data.pair_playbooks]);

  const openCurrency = useCallback((item) => {
    if (!item) return;
    const full = (data.currency_strength || []).find((row) => row.currency === item.currency) || item;
    setModal({ type: "currency", payload: full });
  }, [data.currency_strength]);

  const openEvent = useCallback((item) => {
    if (!item) return;
    const full = (data.economic_events || []).find((row) => row.id === item.id) || item;
    setModal({ type: "event", payload: full });
  }, [data.economic_events]);

  const timeZoneOptions = Array.from(new Set([timeZone, detectTimeZone(), ...COMMON_TIME_ZONES].filter(isValidTimeZone)));
  const headerRankedCurrencies = [...(data.currency_strength || [])].sort((a, b) => Number(b.score || 0) - Number(a.score || 0));
  const headerStrongest = headerRankedCurrencies[0] || null;
  const headerWeakest = headerRankedCurrencies[headerRankedCurrencies.length - 1] || null;
  const headerStrongMeta = currencyMeta(headerStrongest);
  const headerWeakMeta = currencyMeta(headerWeakest);
  const headerSpread = headerStrongest && headerWeakest
    ? Number(headerStrongest.score || 0) - Number(headerWeakest.score || 0)
    : 0;



  return (

    <>

      <style>{`
        .fmx-page,
        .fmx-page *,
        .fmx-page *::before,
        .fmx-page *::after { box-sizing: border-box; }

        .fmx-page {
          --bg: #f4f6fb;
          --bg-elevated: rgba(255,255,255,.72);
          --card: rgba(255,255,255,.92);
          --card-solid: #ffffff;
          --soft: #f7f8fc;
          --soft-2: #f1f3f9;
          --text: #121522;
          --sub: #596176;
          --muted: #8a93a8;
          --line: rgba(34,42,67,.10);
          --line-strong: rgba(34,42,67,.16);
          --purple: #7357ef;
          --purple-2: #9b79ff;
          --purple-deep: #563bd6;
          --purple-soft: rgba(115,87,239,.10);
          --blue: #2d70e8;
          --blue-soft: rgba(45,112,232,.10);
          --green: #12956f;
          --green-soft: rgba(18,149,111,.10);
          --red: #d84f68;
          --red-soft: rgba(216,79,104,.10);
          --amber: #c98216;
          --amber-soft: rgba(201,130,22,.11);
          --shadow-sm: 0 1px 2px rgba(18,21,34,.03), 0 8px 22px rgba(18,21,34,.04);
          --shadow-md: 0 18px 50px -30px rgba(23,29,52,.34);
          --shadow-lg: 0 32px 80px -38px rgba(25,22,64,.38);
          min-height: 100vh;
          padding: 22px 24px 40px;
          background:
            radial-gradient(circle at 8% -6%, rgba(115,87,239,.12), transparent 27rem),
            radial-gradient(circle at 94% 8%, rgba(45,112,232,.08), transparent 25rem),
            linear-gradient(180deg, #f8f9fd 0%, var(--bg) 45%, #f6f7fb 100%);
          color: var(--text);
          font-family: Inter, Manrope, "SF Pro Display", "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 14px;
          line-height: 1.5;
          overflow-x: hidden;
          -webkit-font-smoothing: antialiased;
          text-rendering: optimizeLegibility;
        }

        html.dark .fmx-page,
        body.dark .fmx-page,
        .dark .fmx-page,
        [data-theme="dark"] .fmx-page {
          --bg: #090d16;
          --bg-elevated: rgba(16,22,35,.78);
          --card: rgba(18,25,40,.90);
          --card-solid: #121928;
          --soft: #0f1624;
          --soft-2: #111a2a;
          --text: #f6f7fb;
          --sub: #b4bdd0;
          --muted: #7f8aa1;
          --line: rgba(255,255,255,.075);
          --line-strong: rgba(255,255,255,.13);
          --purple: #9a82ff;
          --purple-2: #b39fff;
          --purple-deep: #7e63f7;
          --purple-soft: rgba(154,130,255,.13);
          --blue: #6aa5ff;
          --blue-soft: rgba(106,165,255,.12);
          --green: #4ad6a4;
          --green-soft: rgba(74,214,164,.11);
          --red: #ff768b;
          --red-soft: rgba(255,118,139,.11);
          --amber: #f7b75b;
          --amber-soft: rgba(247,183,91,.11);
          --shadow-sm: 0 1px 0 rgba(255,255,255,.02), 0 12px 28px rgba(0,0,0,.12);
          --shadow-md: 0 24px 70px -38px rgba(0,0,0,.75);
          --shadow-lg: 0 34px 90px -42px rgba(0,0,0,.86);
          background:
            radial-gradient(circle at 7% -4%, rgba(127,89,255,.18), transparent 29rem),
            radial-gradient(circle at 95% 4%, rgba(43,113,255,.11), transparent 27rem),
            linear-gradient(180deg, #0b101b 0%, var(--bg) 48%, #090d16 100%);
        }

        .fmx-page button,
        .fmx-page input,
        .fmx-page select { font: inherit; }
        .fmx-page button { color: inherit; }
        .fmx-page button:focus-visible,
        .fmx-page input:focus-visible,
        .fmx-page select:focus-visible {
          outline: 3px solid rgba(115,87,239,.18);
          outline-offset: 2px;
        }
        .fmx-page svg { width: 1.15em; height: 1.15em; stroke-width: 1.85; }

        .fmx-shell { max-width: 1580px; margin: 0 auto; }

        .fmx-card {
          position: relative;
          min-width: 0;
          padding: 22px;
          border: 1px solid var(--line);
          border-radius: 22px;
          background: var(--card);
          box-shadow: var(--shadow-md);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          overflow: hidden;
        }
        .fmx-card::before {
          content: "";
          position: absolute;
          inset: 0 0 auto;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,.72), transparent);
          pointer-events: none;
        }
        html.dark .fmx-card::before,
        body.dark .fmx-card::before,
        .dark .fmx-card::before,
        [data-theme="dark"] .fmx-card::before { opacity: .12; }

        .fmx-topbar {
          position: relative;
          isolation: isolate;
          display: grid;
          grid-template-columns: minmax(0,1fr) auto;
          gap: 22px;
          align-items: center;
          margin-bottom: 14px;
          padding: 22px 24px;
          border: 1px solid var(--line);
          border-radius: 24px;
          background: var(--bg-elevated);
          box-shadow: var(--shadow-lg);
          backdrop-filter: blur(22px);
          -webkit-backdrop-filter: blur(22px);
          overflow: hidden;
        }
        .fmx-topbar::before {
          content: "";
          position: absolute;
          z-index: -1;
          width: 340px;
          height: 340px;
          right: -150px;
          top: -220px;
          border-radius: 999px;
          background: radial-gradient(circle, rgba(115,87,239,.20), rgba(115,87,239,0) 68%);
          pointer-events: none;
        }
        .fmx-topbar::after {
          content: "";
          position: absolute;
          z-index: -1;
          inset: auto 0 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(115,87,239,.32), transparent);
        }
        .fmx-title-wrap { display: flex; align-items: center; gap: 16px; min-width: 0; }
        .fmx-title-icon {
          width: 52px;
          height: 52px;
          display: grid;
          place-items: center;
          flex: 0 0 52px;
          border-radius: 17px;
          background: linear-gradient(145deg, #6648e7 0%, #8167f5 52%, #a78bfa 100%);
          color: #fff;
          box-shadow: 0 14px 30px -14px rgba(93,65,210,.72), inset 0 1px 0 rgba(255,255,255,.34);
        }
        .fmx-title-icon svg { width: 24px; height: 24px; }
        .fmx-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 5px;
          color: var(--purple);
          font-size: 10px;
          font-weight: 850;
          letter-spacing: .105em;
          text-transform: uppercase;
        }
        .fmx-eyebrow svg { width: 12px; height: 12px; }
        .fmx-title-wrap h1 {
          margin: 0;
          font-size: clamp(25px, 2.1vw, 34px);
          font-weight: 780;
          letter-spacing: -.045em;
          line-height: 1.05;
        }
        .fmx-title-wrap p {
          max-width: 760px;
          margin: 7px 0 0;
          color: var(--sub);
          font-size: 13px;
          line-height: 1.55;
        }

        .fmx-actions { display: flex; align-items: center; gap: 10px; }
        .fmx-zone,
        .fmx-refresh {
          min-height: 44px;
          border-radius: 13px;
          transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease, background .18s ease;
        }
        .fmx-zone {
          position: relative;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0 38px 0 13px;
          border: 1px solid var(--line);
          background: var(--card-solid);
          color: var(--sub);
          box-shadow: var(--shadow-sm);
        }
        .fmx-zone > svg:first-child { color: var(--purple); }
        .fmx-zone select {
          appearance: none;
          min-width: 130px;
          border: 0;
          outline: 0;
          background: transparent;
          color: inherit;
          font-size: 12px;
          font-weight: 680;
          cursor: pointer;
        }
        .fmx-zone svg:last-child { position: absolute; right: 12px; width: 14px; height: 14px; pointer-events: none; }
        .fmx-refresh {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 0 16px;
          border: 1px solid rgba(115,87,239,.18);
          background: linear-gradient(180deg, rgba(115,87,239,.11), rgba(115,87,239,.075));
          color: var(--purple);
          font-size: 12px;
          font-weight: 800;
          box-shadow: 0 10px 24px -18px rgba(115,87,239,.65);
          cursor: pointer;
        }
        .fmx-refresh:hover:not(:disabled), .fmx-zone:hover { transform: translateY(-1px); border-color: rgba(115,87,239,.28); }
        .fmx-refresh:disabled { opacity: .56; cursor: wait; }
        .fmx-spin { animation: fmxSpin .85s linear infinite; }
        @keyframes fmxSpin { to { transform: rotate(360deg); } }
        @keyframes fmxEnter { from { opacity: 0; transform: translateY(7px); } to { opacity: 1; transform: translateY(0); } }

        .fmx-statusbar {
          display: flex;
          align-items: center;
          gap: 18px;
          min-height: 46px;
          margin-bottom: 14px;
          padding: 0 16px;
          border: 1px solid var(--line);
          border-radius: 15px;
          background: var(--card);
          box-shadow: var(--shadow-sm);
          color: var(--sub);
          font-size: 11px;
          backdrop-filter: blur(15px);
        }
        .fmx-statusbar > span { display: inline-flex; align-items: center; gap: 7px; white-space: nowrap; }
        .fmx-statusbar .is-live { color: var(--green); font-weight: 800; }
        .fmx-statusbar .is-error { color: var(--amber); font-weight: 800; }
        .fmx-statusbar i { width: 7px; height: 7px; border-radius: 999px; background: currentColor; box-shadow: 0 0 0 4px color-mix(in srgb, currentColor 12%, transparent); }
        .fmx-statusbar strong { color: var(--text); font-weight: 750; }
        .fmx-statusbar em { margin-left: auto; padding: 5px 9px; border-radius: 999px; background: var(--soft); color: var(--muted); font-size: 10px; font-style: normal; font-weight: 750; }

        .fmx-tabs {
          display: flex;
          gap: 6px;
          margin-bottom: 16px;
          padding: 6px;
          border: 1px solid var(--line);
          border-radius: 16px;
          background: var(--card);
          box-shadow: var(--shadow-sm);
          overflow-x: auto;
          scrollbar-width: none;
        }
        .fmx-tabs::-webkit-scrollbar { display: none; }
        .fmx-tabs button {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-height: 39px;
          padding: 0 14px;
          border: 1px solid transparent;
          border-radius: 11px;
          background: transparent;
          color: var(--sub);
          font-size: 12px;
          font-weight: 720;
          cursor: pointer;
          white-space: nowrap;
          transition: .18s ease;
        }
        .fmx-tabs button svg { width: 15px; height: 15px; }
        .fmx-tabs button:hover { background: var(--soft); color: var(--text); }
        .fmx-tabs button.is-active {
          border-color: rgba(115,87,239,.14);
          background: linear-gradient(180deg, rgba(115,87,239,.13), rgba(115,87,239,.08));
          color: var(--purple);
          box-shadow: inset 0 1px 0 rgba(255,255,255,.38), 0 8px 18px -16px rgba(115,87,239,.8);
        }

        .fmx-banner {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin-bottom: 14px;
          padding: 12px 14px;
          border: 1px solid rgba(201,130,22,.20);
          border-radius: 14px;
          background: var(--amber-soft);
          color: var(--sub);
          font-size: 12px;
          line-height: 1.55;
        }
        .fmx-banner svg { flex: 0 0 auto; margin-top: 2px; color: var(--amber); }
        .fmx-banner strong { color: var(--text); }

        .fmx-section-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 18px; }
        .fmx-section-title { display: flex; align-items: flex-start; gap: 11px; min-width: 0; }
        .fmx-section-title > span {
          width: 38px;
          height: 38px;
          display: grid;
          place-items: center;
          flex: 0 0 38px;
          border: 1px solid rgba(115,87,239,.12);
          border-radius: 12px;
          background: var(--purple-soft);
          color: var(--purple);
        }
        .fmx-section-title > span svg { width: 17px; height: 17px; }
        .fmx-section-title h2 { margin: 0; font-size: 17px; font-weight: 760; letter-spacing: -.025em; line-height: 1.25; }
        .fmx-section-title p { margin: 4px 0 0; color: var(--muted); font-size: 11px; line-height: 1.5; }

        .fmx-summary-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0,1fr));
          gap: 12px;
          margin-bottom: 14px;
          animation: fmxEnter .30s ease both;
        }
        .fmx-kpi-card {
          position: relative;
          display: grid;
          grid-template-columns: 42px minmax(0,1fr);
          gap: 12px;
          align-items: start;
          min-height: 136px;
          padding: 17px;
          border: 1px solid var(--line);
          border-radius: 19px;
          background: var(--card);
          box-shadow: var(--shadow-sm);
          overflow: hidden;
          transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease;
        }
        .fmx-kpi-card::after {
          content: "";
          position: absolute;
          width: 96px;
          height: 96px;
          right: -48px;
          bottom: -58px;
          border-radius: 999px;
          background: var(--purple-soft);
          opacity: .6;
          pointer-events: none;
        }
        .fmx-kpi-card:hover { transform: translateY(-2px); border-color: var(--line-strong); box-shadow: var(--shadow-md); }
        .fmx-kpi-card.is-open { border-color: rgba(18,149,111,.20); background: linear-gradient(145deg, var(--card), var(--green-soft)); }
        .fmx-kpi-card.is-caution { border-color: rgba(201,130,22,.20); background: linear-gradient(145deg, var(--card), var(--amber-soft)); }
        .fmx-kpi-card.is-blocked { border-color: rgba(216,79,104,.20); background: linear-gradient(145deg, var(--card), var(--red-soft)); }
        .fmx-kpi-icon {
          width: 42px;
          height: 42px;
          display: grid;
          place-items: center;
          border: 1px solid rgba(18,149,111,.10);
          border-radius: 13px;
          background: var(--green-soft);
          color: var(--green);
        }
        .fmx-kpi-icon svg { width: 18px; height: 18px; }
        .fmx-kpi-icon.is-purple { border-color: rgba(115,87,239,.12); background: var(--purple-soft); color: var(--purple); }
        .fmx-kpi-icon.is-blue { border-color: rgba(45,112,232,.12); background: var(--blue-soft); color: var(--blue); }
        .fmx-kpi-icon.is-amber { border-color: rgba(201,130,22,.12); background: var(--amber-soft); color: var(--amber); }
        .fmx-kpi-card small { display: block; color: var(--muted); font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: .095em; }
        .fmx-kpi-card strong { display: block; margin-top: 6px; font-size: 16px; font-weight: 770; line-height: 1.28; letter-spacing: -.02em; }
        .fmx-kpi-card strong em { font-style: normal; color: var(--purple); }
        .fmx-kpi-card p { margin: 8px 0 0; color: var(--sub); font-size: 11px; line-height: 1.58; }
        .fmx-inline-flag { font-style: normal; font-size: 1.05em; line-height: 1; filter: saturate(.96); }
        .fmx-flag-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          line-height: 0;
          vertical-align: -2px;
        }
        .fmx-flag-icon svg {
          display: block;
          width: 100%;
          height: 100%;
          border-radius: 3px;
          box-shadow: 0 0 0 1px rgba(15,23,42,.12), 0 2px 5px rgba(15,23,42,.10);
        }
        .fmx-flag-icon.is-inline { width: 20px; height: 14px; margin-right: 5px; }
        .fmx-flag-icon.is-sm { width: 22px; height: 16px; }
        .fmx-flag-icon.is-card { width: 30px; height: 21px; }
        .fmx-flag-icon.is-hero { width: 48px; height: 34px; }
        html.dark .fmx-flag-icon svg, body.dark .fmx-flag-icon svg, .dark .fmx-flag-icon svg, [data-theme="dark"] .fmx-flag-icon svg {
          box-shadow: 0 0 0 1px rgba(255,255,255,.16), 0 3px 8px rgba(0,0,0,.24);
        }

        .fmx-inline-currency { display: inline-flex; align-items: center; gap: 5px; font-weight: 720; color: var(--text); }
        .fmx-kpi-pairing { display: flex !important; align-items: center; flex-wrap: wrap; gap: 6px; }
        .fmx-kpi-pairing > span { display: inline-flex; align-items: center; gap: 5px; }
        .fmx-kpi-pairing > b { color: var(--muted); font-size: 9px; font-weight: 850; text-transform: uppercase; letter-spacing: .08em; }
        .fmx-symbol-with-flags { display: inline-flex !important; align-items: center; gap: 6px; }
        .fmx-symbol-with-flags > span { font-size: 15px; line-height: 1; }

        .fmx-main-grid {
          display: grid;
          grid-template-columns: minmax(0,1.62fr) minmax(330px,.78fr);
          gap: 14px;
          margin-bottom: 14px;
          animation: fmxEnter .34s .03s ease both;
        }
        .fmx-daily-brief, .fmx-focus-panel { min-height: 264px; }
        .fmx-confidence {
          display: inline-flex;
          align-items: center;
          padding: 6px 10px;
          border: 1px solid rgba(115,87,239,.12);
          border-radius: 999px;
          background: var(--purple-soft);
          color: var(--purple);
          font-size: 10px;
          font-weight: 820;
          white-space: nowrap;
        }
        .fmx-brief-body { display: grid; grid-template-columns: minmax(0,1.15fr) minmax(260px,.85fr); gap: 18px; }
        .fmx-brief-primary { padding-right: 4px; }
        .fmx-brief-primary > strong { display: block; max-width: 760px; font-size: clamp(20px,1.7vw,25px); font-weight: 780; line-height: 1.28; letter-spacing: -.035em; }
        .fmx-brief-primary > p { margin: 10px 0 0; color: var(--sub); font-size: 12px; line-height: 1.72; }
        .fmx-rule-box {
          position: relative;
          margin-top: 17px;
          padding: 13px 14px 13px 17px;
          border: 1px solid var(--line);
          border-radius: 14px;
          background: linear-gradient(135deg, var(--soft), var(--card));
          overflow: hidden;
        }
        .fmx-rule-box::before { content: ""; position: absolute; left: 0; top: 10px; bottom: 10px; width: 3px; border-radius: 999px; background: linear-gradient(var(--purple), var(--purple-2)); }
        .fmx-rule-box span { display: block; color: var(--purple); font-size: 9px; font-weight: 850; text-transform: uppercase; letter-spacing: .1em; }
        .fmx-rule-box strong { display: block; margin-top: 5px; font-size: 11px; font-weight: 690; line-height: 1.58; }
        .fmx-reasons { display: flex; flex-direction: column; gap: 8px; }
        .fmx-reasons > div { display: flex; gap: 9px; padding: 10px 11px; border: 1px solid var(--line); border-radius: 12px; background: var(--soft); color: var(--sub); font-size: 10px; line-height: 1.5; }
        .fmx-reasons svg { flex: 0 0 15px; width: 15px; height: 15px; margin-top: 1px; color: var(--purple); }
        .fmx-reasons p { color: var(--muted); font-size: 11px; }
        .fmx-focus-card {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 18px;
          border: 1px solid var(--line);
          border-radius: 18px;
          background: linear-gradient(145deg, var(--soft), var(--card));
          box-shadow: inset 0 1px 0 rgba(255,255,255,.24);
          text-align: left;
          cursor: pointer;
          transition: .18s ease;
        }
        .fmx-focus-card:hover { transform: translateY(-2px); border-color: rgba(115,87,239,.24); box-shadow: var(--shadow-sm); }
        .fmx-focus-card small { display: block; color: var(--muted); font-size: 9px; font-weight: 760; text-transform: uppercase; letter-spacing: .06em; }
        .fmx-focus-main { min-width: 0; display: flex; flex-direction: column; gap: 8px; }
        .fmx-focus-pair-pill {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          width: fit-content;
          max-width: 100%;
          padding: 10px 12px;
          border-radius: 14px;
          border: 1px solid rgba(115,87,239,.14);
          background: rgba(115,87,239,.05);
          box-shadow: inset 0 1px 0 rgba(255,255,255,.24);
        }
        .fmx-focus-flag {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
        }
        .fmx-focus-code {
          display: inline-block;
          font-size: 18px;
          font-weight: 800;
          line-height: 1;
          letter-spacing: -.03em;
          white-space: nowrap;
          color: var(--text);
        }
        .fmx-focus-separator {
          color: var(--muted);
          font-size: 16px;
          font-weight: 800;
          line-height: 1;
          flex: 0 0 auto;
        }
        .fmx-focus-meta {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr)) auto;
          align-items: center;
          gap: 12px;
        }
        .fmx-focus-stat {
          min-width: 0;
          padding: 10px 12px;
          border: 1px solid var(--line);
          border-radius: 14px;
          background: rgba(255,255,255,.46);
        }
        .fmx-focus-stat strong {
          display: block;
          margin-top: 5px;
          font-size: 15px;
          font-weight: 780;
          white-space: nowrap;
          color: var(--text);
        }
        .fmx-focus-arrow {
          width: 42px;
          height: 42px;
          display: grid;
          place-items: center;
          border-radius: 14px;
          border: 1px solid rgba(115,87,239,.14);
          background: rgba(115,87,239,.05);
          color: var(--purple);
          flex: 0 0 42px;
        }
        .fmx-focus-arrow svg {
          width: 18px;
          height: 18px;
        }
        .fmx-event-time small { display: inline-flex !important; align-items: center; gap: 4px; }
        .fmx-pair-identity strong { display: inline-flex !important; align-items: center; gap: 6px; }
        .fmx-pair-identity strong i { font-style: normal; font-size: 14px; line-height: 1; }
        .fmx-calendar-currency { display: flex !important; align-items: center; gap: 8px; }
        .fmx-calendar-currency > i { width: 27px; height: 27px; display: grid; place-items: center; flex: 0 0 27px; border: 1px solid var(--line); border-radius: 9px; background: var(--card); font-style: normal; font-size: 15px; box-shadow: var(--shadow-sm); }
        .fmx-calendar-currency > span { min-width: 0; }
        .fmx-calendar-currency small { margin-top: 1px !important; font-size: 7.5px !important; text-transform: uppercase; letter-spacing: .06em; }

        .fmx-strength-section { padding: 24px; }
        .fmx-strength-section .fmx-section-head { margin-bottom: 16px; }
        .fmx-strength-legend { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .fmx-strength-legend span {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          min-height: 26px;
          padding: 0 8px;
          border: 1px solid var(--line);
          border-radius: 999px;
          background: var(--soft);
          color: var(--muted);
          font-size: 8px;
          font-weight: 800;
        }
        .fmx-strength-legend i { width: 6px; height: 6px; border-radius: 999px; background: currentColor; }
        .fmx-strength-legend .is-up { color: var(--green); background: var(--green-soft); }
        .fmx-strength-legend .is-down { color: var(--red); background: var(--red-soft); }
        .fmx-strength-legend .is-neutral { color: var(--muted); }

        .fmx-strength-overview {
          display: grid;
          grid-template-columns: minmax(0,1fr) 118px;
          gap: 16px;
          align-items: center;
          margin-bottom: 14px;
          padding: 15px 16px;
          border: 1px solid rgba(115,87,239,.13);
          border-radius: 17px;
          background:
            linear-gradient(115deg, rgba(115,87,239,.08), rgba(45,112,232,.035) 54%, transparent),
            var(--soft);
        }
        .fmx-strength-kicker {
          display: block;
          color: var(--purple);
          font-size: 8px;
          font-weight: 900;
          letter-spacing: .095em;
          text-transform: uppercase;
        }
        .fmx-strength-overview-copy > strong {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 4px;
          color: var(--text);
          font-size: 17px;
          font-weight: 820;
          letter-spacing: -.02em;
        }
        .fmx-strength-overview-copy > strong span { display: inline-flex; align-items: center; gap: 5px; }
        .fmx-strength-overview-copy > strong small {
          color: var(--muted);
          font-size: 9px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .05em;
        }
        .fmx-strength-overview-copy p {
          margin: 6px 0 0;
          color: var(--sub);
          font-size: 10.5px;
          line-height: 1.55;
        }
        .fmx-strength-overview-copy p b { color: var(--text); }
        .fmx-strength-spread {
          min-height: 74px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(115,87,239,.13);
          border-radius: 15px;
          background: var(--card-solid);
          box-shadow: var(--shadow-sm);
        }
        .fmx-strength-spread small {
          color: var(--muted);
          font-size: 8px;
          font-weight: 850;
          letter-spacing: .06em;
          text-transform: uppercase;
        }
        .fmx-strength-spread strong {
          margin-top: 2px;
          color: var(--purple);
          font-size: 24px;
          font-weight: 850;
          letter-spacing: -.04em;
          line-height: 1;
        }
        .fmx-strength-spread span { margin-top: 2px; color: var(--muted); font-size: 8px; }

        .fmx-currency-strip { display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 10px; }
        .fmx-currency-row {
          --currency-accent: var(--purple);
          --currency-soft: var(--purple-soft);
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 10px;
          min-width: 0;
          min-height: 158px;
          padding: 14px;
          border: 1px solid var(--line);
          border-radius: 17px;
          background: linear-gradient(145deg, var(--card-solid), var(--soft));
          text-align: left;
          cursor: pointer;
          overflow: hidden;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.48);
          transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease, background .18s ease;
        }
        .fmx-currency-row::before {
          content: "";
          position: absolute;
          inset: 0 0 auto;
          height: 3px;
          background: linear-gradient(90deg, var(--currency-accent), transparent 82%);
          opacity: .62;
        }
        .fmx-currency-row.is-up { --currency-accent: var(--green); --currency-soft: var(--green-soft); }
        .fmx-currency-row.is-down { --currency-accent: var(--red); --currency-soft: var(--red-soft); }
        .fmx-currency-row.is-neutral { --currency-accent: var(--purple); --currency-soft: var(--purple-soft); }
        .fmx-currency-row:hover {
          transform: translateY(-3px);
          border-color: color-mix(in srgb, var(--currency-accent) 24%, var(--line));
          background: linear-gradient(145deg, var(--card-solid), var(--currency-soft));
          box-shadow: 0 18px 36px -28px color-mix(in srgb, var(--currency-accent) 42%, transparent);
        }

        .fmx-currency-identity { display: grid; grid-template-columns: 42px minmax(0,1fr); gap: 10px; align-items: center; }
        .fmx-currency-flag {
          width: 42px;
          height: 42px;
          display: grid;
          place-items: center;
          border: 1px solid var(--line);
          border-radius: 13px;
          background: var(--card-solid);
          font-size: 23px;
          line-height: 1;
          box-shadow: 0 8px 18px -14px rgba(17,24,39,.45), inset 0 1px 0 rgba(255,255,255,.6);
        }
        .fmx-currency-flag.is-large { width: 46px; height: 46px; border-radius: 14px; font-size: 25px; }
        .fmx-currency-flag .fmx-flag-icon { margin: 0; }
        .fmx-currency-overline {
          display: block;
          color: var(--muted);
          font-size: 8.5px;
          font-weight: 850;
          letter-spacing: .05em;
          text-transform: uppercase;
        }
        .fmx-currency-identity strong {
          display: block;
          margin-top: 1px;
          color: var(--text);
          font-size: 15px;
          font-weight: 850;
          letter-spacing: -.02em;
          line-height: 1.05;
        }
        .fmx-currency-identity small {
          display: block;
          margin-top: 3px;
          color: var(--sub);
          font-size: 10px;
          line-height: 1.25;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .fmx-currency-scoreline { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
        .fmx-currency-scoreline > span {
          color: var(--sub);
          font-size: 10px;
          font-weight: 760;
        }
        .fmx-currency-scoreline > strong {
          color: var(--currency-accent);
          font-size: 18px;
          font-weight: 850;
          letter-spacing: -.035em;
          line-height: 1;
        }
        .fmx-currency-scoreline > strong small {
          margin-left: 2px;
          color: var(--muted);
          font-size: 8px;
          font-weight: 700;
        }

        .fmx-strength-track,
        .fmx-currency-card-track {
          position: relative;
          height: 7px;
          overflow: hidden;
          border-radius: 999px;
          background: var(--line);
        }
        .fmx-strength-track i,
        .fmx-currency-card-track i {
          display: block;
          height: 100%;
          border-radius: inherit;
          background: linear-gradient(90deg, color-mix(in srgb, var(--currency-accent) 72%, #fff), var(--currency-accent));
          box-shadow: 0 0 12px color-mix(in srgb, var(--currency-accent) 25%, transparent);
        }
        .fmx-strength-track b,
        .fmx-currency-card-track b,
        .fmx-breakdown-track b {
          position: absolute;
          left: 50%;
          top: -2px;
          bottom: -2px;
          width: 1px;
          background: color-mix(in srgb, var(--text) 25%, transparent);
          opacity: .65;
        }

        .fmx-currency-foot {
          display: grid;
          grid-template-columns: auto 1fr 16px;
          gap: 7px;
          align-items: center;
          margin-top: auto;
          color: var(--muted);
          font-size: 9px;
          font-weight: 720;
        }
        .fmx-currency-foot span:first-child {
          width: max-content;
          padding: 4px 6px;
          border-radius: 999px;
          background: var(--currency-soft);
          color: var(--currency-accent);
          font-weight: 820;
        }
        .fmx-currency-foot span:nth-child(2) { text-align: right; }
        .fmx-currency-foot svg { color: var(--currency-accent); width: 14px; height: 14px; }

        .fmx-rank { width: 24px; height: 24px; display: grid; place-items: center; border-radius: 999px; background: var(--purple-soft); color: var(--purple); font-size: 9px; font-weight: 850; }
        .fmx-score-bar { height: 6px; overflow: hidden; border-radius: 999px; background: var(--line); }
        .fmx-score-bar i, .fmx-main-bar i, .fmx-health-track i, .fmx-quality-card > div i { display: block; height: 100%; border-radius: inherit; background: linear-gradient(90deg, var(--purple-deep), var(--purple-2)); box-shadow: 0 0 14px rgba(115,87,239,.24); }

        .fmx-lower-grid { display: grid; grid-template-columns: 1fr; gap: 14px; margin-top: 14px; animation: fmxEnter .36s .05s ease both; }
        .fmx-event-mini-list { display: flex; flex-direction: column; }
        .fmx-event-mini-list button {
          display: grid;
          grid-template-columns: 78px minmax(0,1fr) 66px 22px;
          gap: 11px;
          align-items: center;
          min-height: 58px;
          padding: 9px 5px;
          border: 0;
          border-top: 1px solid var(--line);
          background: transparent;
          text-align: left;
          cursor: pointer;
          transition: background .16s ease, padding .16s ease;
        }
        .fmx-event-mini-list button:first-child { border-top: 0; }
        .fmx-event-mini-list button:hover { margin-inline: -8px; padding-inline: 13px; border-radius: 12px; background: var(--soft); }
        .fmx-event-time strong, .fmx-event-copy strong { display: block; font-size: 11px; font-weight: 760; }
        .fmx-event-time small, .fmx-event-copy small { display: block; margin-top: 3px; color: var(--muted); font-size: 9px; }
        .fmx-impact { display: inline-flex; justify-content: center; align-items: center; min-height: 23px; padding: 4px 8px; border-radius: 999px; font-size: 8px; font-weight: 850; letter-spacing: .025em; }
        .fmx-impact.is-fort { color: var(--red); background: var(--red-soft); }
        .fmx-impact.is-moyen { color: var(--amber); background: var(--amber-soft); }
        .fmx-impact.is-faible { color: var(--green); background: var(--green-soft); }
        .fmx-health-meter { display: grid; grid-template-columns: 92px 1fr; gap: 12px; align-items: center; padding: 8px 0 3px; }
        .fmx-health-number { grid-row: span 2; }
        .fmx-health-number strong { display: block; font-size: 31px; font-weight: 780; letter-spacing: -.04em; }
        .fmx-health-number span { color: var(--muted); font-size: 9px; font-weight: 760; text-transform: uppercase; letter-spacing: .07em; }
        .fmx-health-track { height: 8px; overflow: hidden; border-radius: 999px; background: var(--line); }
        .fmx-health-meter p { margin: 0; color: var(--sub); font-size: 10px; line-height: 1.5; }
        .fmx-health-facts { display: flex; flex-direction: column; gap: 8px; margin-top: 16px; }
        .fmx-health-facts span { display: flex; align-items: center; gap: 8px; padding: 7px 9px; border-radius: 10px; background: var(--soft); color: var(--sub); font-size: 10px; }
        .fmx-health-facts svg { flex: 0 0 14px; width: 14px; height: 14px; color: var(--purple); }

        .fmx-table-wrap { overflow: auto; border: 1px solid var(--line); border-radius: 15px; background: var(--card-solid); }
        .fmx-pair-table-head, .fmx-pair-table-row { min-width: 940px; display: grid; grid-template-columns: 120px 120px 90px 130px minmax(320px,1fr) 30px; gap: 13px; align-items: center; }
        .fmx-pair-table-head { padding: 11px 13px; background: var(--soft); color: var(--muted); font-size: 9px; font-weight: 820; text-transform: uppercase; letter-spacing: .08em; }
        .fmx-pair-table-row { width: 100%; padding: 13px; border: 0; border-top: 1px solid var(--line); background: transparent; text-align: left; cursor: pointer; transition: background .15s ease; }
        .fmx-pair-table-row:hover { background: var(--soft); }
        .fmx-pair-table-row strong { display: block; font-size: 12px; font-weight: 760; }
        .fmx-pair-table-row small { display: block; margin-top: 3px; color: var(--muted); font-size: 9px; }
        .fmx-pair-table-row > span:last-child { color: var(--purple); }
        .fmx-bias { display: inline-flex; align-items: center; gap: 5px; width: max-content; padding: 5px 8px; border-radius: 999px; font-size: 9px; font-weight: 830; }
        .fmx-bias svg { width: 13px; height: 13px; }
        .fmx-bias.is-up { color: var(--green); background: var(--green-soft); }
        .fmx-bias.is-down { color: var(--red); background: var(--red-soft); }
        .fmx-bias.is-neutral { color: var(--muted); background: var(--soft); }
        .fmx-thesis-cell { color: var(--sub); font-size: 10px; line-height: 1.55; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

        .fmx-currency-grid { display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 12px; }
        .fmx-currency-card {
          --currency-accent: var(--purple);
          --currency-soft: var(--purple-soft);
          position: relative;
          min-width: 0;
          padding: 16px;
          border: 1px solid var(--line);
          border-radius: 18px;
          background: linear-gradient(145deg, var(--card-solid), var(--soft));
          text-align: left;
          cursor: pointer;
          overflow: hidden;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.42);
          transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease, background .18s ease;
        }
        .fmx-currency-card::before {
          content: "";
          position: absolute;
          inset: 0 0 auto;
          height: 3px;
          background: linear-gradient(90deg, var(--currency-accent), transparent 78%);
          opacity: .72;
        }
        .fmx-currency-card.is-up { --currency-accent: var(--green); --currency-soft: var(--green-soft); }
        .fmx-currency-card.is-down { --currency-accent: var(--red); --currency-soft: var(--red-soft); }
        .fmx-currency-card.is-neutral { --currency-accent: var(--purple); --currency-soft: var(--purple-soft); }
        .fmx-currency-card:hover {
          transform: translateY(-3px);
          border-color: color-mix(in srgb, var(--currency-accent) 24%, var(--line));
          background: linear-gradient(145deg, var(--card-solid), var(--currency-soft));
          box-shadow: 0 20px 42px -30px color-mix(in srgb, var(--currency-accent) 44%, transparent);
        }
        .fmx-currency-card-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
        .fmx-currency-card-id { display: grid; grid-template-columns: 46px minmax(0,1fr); gap: 10px; align-items: center; min-width: 0; }
        .fmx-currency-card-id > div { min-width: 0; }
        .fmx-currency-card-id span {
          display: block;
          color: var(--muted);
          font-size: 8.5px;
          font-weight: 850;
          letter-spacing: .05em;
          text-transform: uppercase;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .fmx-currency-card-id strong {
          display: block;
          margin-top: 1px;
          color: var(--text);
          font-size: 16px;
          font-weight: 850;
          letter-spacing: -.025em;
          line-height: 1.05;
        }
        .fmx-currency-card-id small {
          display: block;
          margin-top: 3px;
          color: var(--sub);
          font-size: 10px;
          line-height: 1.25;
        }

        .fmx-currency-score-orb {
          --orb-accent: var(--currency-accent);
          position: relative;
          width: 57px;
          height: 57px;
          display: grid;
          place-items: center;
          flex: 0 0 57px;
          border-radius: 999px;
          background: conic-gradient(var(--orb-accent) var(--score-angle), var(--line) 0);
          box-shadow: 0 8px 20px -15px color-mix(in srgb, var(--orb-accent) 50%, transparent);
        }
        .fmx-currency-score-orb::after {
          content: "";
          position: absolute;
          inset: 5px;
          border: 1px solid var(--line);
          border-radius: inherit;
          background: var(--card-solid);
        }
        .fmx-currency-score-orb > div {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: baseline;
          gap: 1px;
        }
        .fmx-currency-score-orb strong { color: var(--text); font-size: 17px; font-weight: 850; letter-spacing: -.04em; }
        .fmx-currency-score-orb small { color: var(--muted); font-size: 7px; font-weight: 760; }

        .fmx-currency-card-reading { margin-top: 14px; }
        .fmx-currency-card-reading > span {
          display: inline-flex;
          min-height: 23px;
          align-items: center;
          padding: 0 7px;
          border-radius: 999px;
          background: var(--currency-soft);
          color: var(--currency-accent);
          font-size: 9px;
          font-weight: 850;
        }
        .fmx-currency-card-reading p {
          min-height: 52px;
          margin: 8px 0 0;
          color: var(--sub);
          font-size: 10.5px;
          line-height: 1.6;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .fmx-currency-driver {
          margin: 11px 0;
          padding: 9px 10px;
          border: 1px solid var(--line);
          border-radius: 11px;
          background: var(--soft);
        }
        .fmx-currency-driver small {
          display: block;
          color: var(--muted);
          font-size: 8.5px;
          font-weight: 850;
          letter-spacing: .05em;
          text-transform: uppercase;
        }
        .fmx-currency-driver strong {
          display: block;
          margin-top: 3px;
          color: var(--text);
          font-size: 10px;
          font-weight: 720;
          line-height: 1.35;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .fmx-currency-card-track { --currency-accent: inherit; margin-top: 2px; }
        .fmx-main-bar { height: 7px; overflow: hidden; border-radius: 999px; background: var(--line); }
        .fmx-currency-meta {
          display: grid;
          grid-template-columns: repeat(3,auto) 1fr;
          gap: 5px;
          align-items: center;
          margin-top: 10px;
        }
        .fmx-currency-meta span {
          width: max-content;
          padding: 4px 6px;
          border: 1px solid color-mix(in srgb, var(--currency-accent) 12%, var(--line));
          border-radius: 999px;
          background: var(--currency-soft);
          color: var(--currency-accent);
          font-size: 8.5px;
          font-weight: 780;
        }
        .fmx-currency-meta svg {
          justify-self: end;
          color: var(--currency-accent);
          width: 15px;
          height: 15px;
        }

        .fmx-toolbar { display: flex; gap: 9px; margin-bottom: 13px; }
        .fmx-search, .fmx-select { position: relative; display: flex; align-items: center; gap: 8px; min-height: 44px; padding: 0 12px; border: 1px solid var(--line); border-radius: 12px; background: var(--soft); transition: border-color .16s ease, box-shadow .16s ease; }
        .fmx-search:focus-within, .fmx-select:focus-within { border-color: rgba(115,87,239,.30); box-shadow: 0 0 0 3px rgba(115,87,239,.08); }
        .fmx-search { flex: 1; }
        .fmx-search > svg, .fmx-select > svg:first-child { color: var(--muted); }
        .fmx-search input, .fmx-select select { width: 100%; border: 0; outline: 0; background: transparent; color: var(--text); font-size: 11px; }
        .fmx-search input::placeholder { color: var(--muted); }
        .fmx-select { min-width: 180px; padding-right: 34px; }
        .fmx-select select { appearance: none; font-weight: 650; }
        .fmx-select svg:last-child { position: absolute; right: 10px; width: 14px; height: 14px; pointer-events: none; }
        .fmx-calendar-table { overflow: auto; border: 1px solid var(--line); border-radius: 15px; background: var(--card-solid); }
        .fmx-calendar-head, .fmx-calendar-row { min-width: 940px; display: grid; grid-template-columns: 106px 72px minmax(290px,1fr) 86px 96px 96px 26px; gap: 11px; align-items: center; }
        .fmx-calendar-head { padding: 11px 12px; background: var(--soft); color: var(--muted); font-size: 9px; font-weight: 820; text-transform: uppercase; letter-spacing: .075em; }
        .fmx-calendar-row { width: 100%; padding: 13px 12px; border: 0; border-top: 1px solid var(--line); background: transparent; text-align: left; cursor: pointer; transition: background .15s ease; }
        .fmx-calendar-row:hover { background: var(--soft); }
        .fmx-calendar-row strong { display: block; font-size: 11px; font-weight: 760; }
        .fmx-calendar-row small { display: block; margin-top: 3px; color: var(--muted); font-size: 9px; }
        .fmx-calendar-row > span:last-child { color: var(--purple); }

        .fmx-macro-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 14px; animation: fmxEnter .3s ease both; }
        .fmx-bank-list { display: flex; flex-direction: column; }
        .fmx-bank-list article { display: grid; grid-template-columns: 190px minmax(0,1fr) 170px; gap: 12px; align-items: center; padding: 12px 4px; border-top: 1px solid var(--line); }
        .fmx-bank-list article:first-child { border-top: 0; }
        .fmx-bank-list strong { display: block; font-size: 12px; font-weight: 780; }
        .fmx-bank-list small { color: var(--muted); font-size: 9px; }
        .fmx-bank-list span { color: var(--sub); font-size: 10px; }
        .fmx-rate-grid { display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 8px; }
        .fmx-rate-grid article { padding: 12px; border: 1px solid var(--line); border-radius: 12px; background: var(--soft); }
        .fmx-rate-grid small { display: block; color: var(--muted); font-size: 9px; font-weight: 720; }
        .fmx-rate-grid strong { display: block; margin-top: 4px; font-size: 14px; font-weight: 790; }
        .fmx-geo-line { display: flex; gap: 10px; margin-top: 12px; padding: 12px; border: 1px solid var(--line); border-radius: 13px; background: var(--soft); }
        .fmx-geo-line > svg { flex: 0 0 17px; width: 17px; height: 17px; margin-top: 2px; color: var(--purple); }
        .fmx-geo-line strong { font-size: 11px; }
        .fmx-geo-line p { margin: 4px 0 0; color: var(--sub); font-size: 10px; line-height: 1.58; }
        .fmx-factor-list { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 8px; }
        .fmx-factor-list span { display: flex; align-items: center; gap: 8px; padding: 10px; border: 1px solid var(--line); border-radius: 11px; background: var(--soft); color: var(--sub); font-size: 10px; }
        .fmx-factor-list svg { flex: 0 0 14px; width: 14px; height: 14px; color: var(--purple); }
        .fmx-quality-card { padding: 12px 8px 5px; text-align: center; }
        .fmx-quality-card > strong { display: block; font-size: 38px; font-weight: 790; letter-spacing: -.045em; }
        .fmx-quality-card > span { color: var(--muted); font-size: 9px; font-weight: 750; text-transform: uppercase; letter-spacing: .08em; }
        .fmx-quality-card > div { height: 8px; margin: 13px 0; overflow: hidden; border-radius: 999px; background: var(--line); }
        .fmx-quality-card > p { margin: 0; color: var(--sub); font-size: 10px; line-height: 1.58; }
        .fmx-muted { color: var(--muted); font-size: 10px; }

        .fmx-empty { min-height: 132px; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 20px; text-align: center; color: var(--muted); }
        .fmx-empty svg { width: 24px; height: 24px; color: var(--purple); opacity: .72; }
        .fmx-empty strong { margin-top: 9px; color: var(--text); font-size: 12px; font-weight: 760; }
        .fmx-empty span { max-width: 440px; margin-top: 4px; font-size: 10px; line-height: 1.5; }
        .fmx-loader { margin-bottom: 14px; padding: 16px; border: 1px solid var(--line); border-radius: 15px; background: var(--card); color: var(--sub); font-size: 11px; box-shadow: var(--shadow-sm); }

        .fmx-modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: grid;
          place-items: center;
          padding: 22px;
          background: rgba(7,10,18,.62);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          animation: fmxEnter .18s ease both;
        }
        .fmx-modal {
          width: min(980px,100%);
          max-height: 88vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border: 1px solid var(--line-strong);
          border-radius: 24px;
          background: var(--card-solid);
          box-shadow: 0 38px 100px rgba(0,0,0,.34);
        }
        .fmx-modal > header { display: flex; justify-content: space-between; gap: 16px; padding: 18px 20px; border-bottom: 1px solid var(--line); background: linear-gradient(180deg, var(--soft), var(--card-solid)); }
        .fmx-modal h3 { margin: 0; font-size: 20px; font-weight: 790; letter-spacing: -.03em; }
        .fmx-modal header p { margin: 5px 0 0; color: var(--muted); font-size: 10px; line-height: 1.45; }
        .fmx-modal header button { width: 38px; height: 38px; display: grid; place-items: center; flex: 0 0 38px; border: 1px solid var(--line); border-radius: 12px; background: var(--card-solid); color: var(--sub); cursor: pointer; transition: .16s ease; }
        .fmx-modal header button:hover { border-color: rgba(115,87,239,.24); color: var(--purple); transform: rotate(2deg); }
        .fmx-modal-body { overflow: auto; padding: 20px; }
        .fmx-detail-grid { display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 10px; }
        .fmx-detail-card { padding: 13px; border: 1px solid var(--line); border-radius: 13px; background: var(--soft); }
        .fmx-detail-card small { display: block; color: var(--muted); font-size: 9px; font-weight: 760; text-transform: uppercase; letter-spacing: .06em; }
        .fmx-detail-card strong { display: block; margin-top: 5px; font-size: 16px; font-weight: 790; }
        .fmx-detail-card p { margin: 6px 0 0; color: var(--sub); font-size: 10px; line-height: 1.52; }
        .fmx-detail-columns { display: grid; grid-template-columns: 1fr 1fr; gap: 11px; margin-top: 11px; }
        .fmx-detail-box { padding: 14px; border: 1px solid var(--line); border-radius: 14px; background: var(--soft); }
        .fmx-detail-box h4 { margin: 0 0 7px; font-size: 12px; font-weight: 780; letter-spacing: -.01em; }
        .fmx-detail-box p, .fmx-detail-box li { color: var(--sub); font-size: 10px; line-height: 1.62; }
        .fmx-detail-box p { margin: 0; }
        .fmx-detail-box ul { margin: 0; padding-left: 17px; }
        .fmx-detail-box li + li { margin-top: 4px; }

        @media (max-width: 1300px) {
          .fmx-summary-grid { grid-template-columns: repeat(2,minmax(0,1fr)); }
          .fmx-currency-strip { grid-template-columns: repeat(4,minmax(0,1fr)); }
          .fmx-currency-grid { grid-template-columns: repeat(2,minmax(0,1fr)); }
        }

        @media (max-width: 980px) {
          .fmx-page { padding: 15px 14px 30px; }
          .fmx-topbar { grid-template-columns: 1fr; gap: 17px; padding: 19px; }
          .fmx-actions { justify-content: flex-start; flex-wrap: wrap; }
          .fmx-title-wrap h1 { font-size: 26px; }
          .fmx-main-grid, .fmx-lower-grid, .fmx-macro-grid { grid-template-columns: 1fr; }
          .fmx-brief-body { grid-template-columns: 1fr; }
          .fmx-detail-columns, .fmx-detail-grid { grid-template-columns: 1fr 1fr; }
          .fmx-bank-list article { grid-template-columns: 1fr; gap: 4px; }
          .fmx-rate-grid { grid-template-columns: repeat(2,minmax(0,1fr)); }
          .fmx-statusbar { gap: 10px; overflow-x: auto; scrollbar-width: none; }
          .fmx-statusbar::-webkit-scrollbar { display: none; }
          .fmx-statusbar em { margin-left: 0; white-space: nowrap; }
        }

        @media (max-width: 640px) {
          .fmx-page { padding: 10px 9px 24px; font-size: 13px; }
          .fmx-topbar { padding: 16px; border-radius: 20px; }
          .fmx-title-wrap { align-items: flex-start; gap: 12px; }
          .fmx-title-icon { width: 46px; height: 46px; flex-basis: 46px; border-radius: 15px; }
          .fmx-title-icon svg { width: 21px; height: 21px; }
          .fmx-title-wrap h1 { font-size: 22px; }
          .fmx-title-wrap p { margin-top: 6px; font-size: 11px; }
          .fmx-eyebrow { font-size: 8px; }
          .fmx-actions { display: grid; grid-template-columns: minmax(0,1fr) auto; width: 100%; }
          .fmx-zone { min-width: 0; }
          .fmx-zone select { min-width: 0; width: 100%; }
          .fmx-refresh { padding-inline: 13px; }
          .fmx-statusbar { min-height: 0; padding: 9px 11px; border-radius: 13px; }
          .fmx-tabs { margin-inline: -1px; }
          .fmx-tabs button { min-height: 38px; padding-inline: 12px; }
          .fmx-card { padding: 16px; border-radius: 18px; }
          .fmx-section-head { margin-bottom: 14px; }
          .fmx-section-title > span { width: 34px; height: 34px; flex-basis: 34px; }
          .fmx-section-title h2 { font-size: 15px; }
          .fmx-section-title p { font-size: 10px; }
          .fmx-summary-grid, .fmx-currency-grid { grid-template-columns: 1fr; }
          .fmx-kpi-card { min-height: auto; padding: 15px; }
          .fmx-main-grid, .fmx-lower-grid { gap: 10px; margin-bottom: 10px; }
          .fmx-brief-primary > strong { font-size: 19px; }
          .fmx-currency-strip { grid-template-columns: repeat(2,minmax(0,1fr)); }
          .fmx-detail-columns, .fmx-detail-grid, .fmx-factor-list { grid-template-columns: 1fr; }
          .fmx-toolbar { flex-direction: column; }
          .fmx-select { min-width: 0; }
          .fmx-rate-grid { grid-template-columns: repeat(2,minmax(0,1fr)); }
          .fmx-modal-backdrop { padding: 10px; align-items: end; }
          .fmx-modal { max-height: 92vh; border-radius: 22px 22px 16px 16px; }
          .fmx-modal > header { padding: 16px; }
          .fmx-modal-body { padding: 16px; }
        }

        @media (max-width: 760px) {
          .fmx-focus-meta {
            grid-template-columns: 1fr 1fr auto;
          }
        }

        @media (max-width: 420px) {
          .fmx-actions { grid-template-columns: 1fr; }
          .fmx-refresh { width: 100%; }
          .fmx-currency-strip { grid-template-columns: 1fr; }
          .fmx-rate-grid { grid-template-columns: 1fr; }
          .fmx-focus-meta {
            grid-template-columns: 1fr;
          }
          .fmx-focus-arrow { justify-self: end; }
          .fmx-focus-pair-pill {
            width: 100%;
            justify-content: center;
          }
        }



        /* ===== Fundamental workspace extensions ===== */
        .fmx-regime-grid,.fmx-bank-detail-grid,.fmx-cot-grid,.fmx-playbook-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
        .fmx-regime-card,.fmx-playbook-card{width:100%;border:1px solid var(--line);border-radius:18px;background:linear-gradient(145deg,var(--soft),var(--card));padding:16px;text-align:left;cursor:pointer;transition:.18s ease}.fmx-regime-card:hover,.fmx-playbook-card:hover{transform:translateY(-2px);border-color:rgba(115,87,239,.25);box-shadow:var(--shadow-sm)}
        .fmx-regime-top{display:grid;grid-template-columns:42px minmax(0,1fr) auto;gap:10px;align-items:center}.fmx-regime-flag{width:42px;height:42px;display:grid;place-items:center;border:1px solid var(--line);border-radius:13px;background:var(--card)}.fmx-regime-top strong{display:block;font-size:16px}.fmx-regime-top small{display:block;margin-top:2px;color:var(--muted);font-size:10px}.fmx-regime-top>b{font-size:22px;color:var(--purple)}.fmx-regime-name{margin-top:14px;font-size:14px;font-weight:800}.fmx-regime-policy{display:flex;align-items:center;gap:7px;margin-top:7px;color:var(--sub);font-size:10px}.fmx-regime-policy svg{width:14px;color:var(--purple)}
        .fmx-pillar-mini-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:14px}.fmx-pillar-mini span{display:flex;justify-content:space-between;gap:8px}.fmx-pillar-mini small{color:var(--muted);font-size:8px}.fmx-pillar-mini b{font-size:9px}.fmx-pillar-mini>div{height:5px;margin-top:4px;border-radius:999px;background:var(--line);overflow:hidden}.fmx-pillar-mini i{display:block;height:100%;border-radius:inherit;background:linear-gradient(90deg,var(--purple),var(--purple-2))}.fmx-regime-footer{display:flex;justify-content:space-between;align-items:center;margin-top:14px;padding-top:10px;border-top:1px solid var(--line);color:var(--muted);font-size:9px}.fmx-regime-footer svg{width:15px;color:var(--purple)}
        .fmx-bank-detail-card{border:1px solid var(--line);border-radius:18px;background:linear-gradient(145deg,var(--soft),var(--card));padding:16px}.fmx-bank-detail-card>header{display:grid;grid-template-columns:42px minmax(0,1fr) auto;gap:10px;align-items:center}.fmx-bank-detail-flag{width:42px;height:42px;display:grid;place-items:center;border:1px solid var(--line);border-radius:13px;background:var(--card)}.fmx-bank-detail-card header strong{display:block;font-size:14px}.fmx-bank-detail-card header small{display:block;margin-top:2px;color:var(--muted);font-size:9px}.fmx-bank-metrics{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-top:14px}.fmx-bank-metrics>div{padding:10px;border:1px solid var(--line);border-radius:12px;background:var(--card)}.fmx-bank-metrics small{display:block;color:var(--muted);font-size:8px}.fmx-bank-metrics strong{display:block;margin-top:4px;font-size:13px}.fmx-bank-drivers{display:grid;gap:7px;margin-top:12px}.fmx-bank-drivers>div{padding:9px 10px;border-radius:11px;background:var(--soft);border:1px solid var(--line)}.fmx-bank-drivers span{display:block;color:var(--purple);font-size:8px;font-weight:800;text-transform:uppercase}.fmx-bank-drivers strong{display:block;margin-top:3px;font-size:10px}.fmx-bank-drivers small{display:block;margin-top:2px;color:var(--muted);font-size:8px}.fmx-bank-next-meeting{display:flex;gap:9px;margin-top:12px;padding:11px;border-radius:12px;background:var(--purple-soft);color:var(--purple)}.fmx-bank-next-meeting svg{width:16px;flex:0 0 16px}.fmx-bank-next-meeting small,.fmx-bank-next-meeting span{display:block;font-size:8px}.fmx-bank-next-meeting strong{display:block;margin:2px 0;font-size:10px;color:var(--text)}
        .fmx-positioning-note{display:grid;grid-template-columns:34px minmax(0,1fr) auto;gap:10px;align-items:center;margin-bottom:14px;padding:12px;border:1px solid var(--line);border-radius:14px;background:var(--soft)}.fmx-positioning-note svg{color:var(--purple)}.fmx-positioning-note strong{display:block;font-size:11px}.fmx-positioning-note span,.fmx-positioning-note small{display:block;color:var(--muted);font-size:9px}.fmx-cot-card{border:1px solid var(--line);border-radius:18px;background:linear-gradient(145deg,var(--soft),var(--card));padding:16px}.fmx-cot-card>header{display:grid;grid-template-columns:38px minmax(0,1fr) auto;gap:9px;align-items:center}.fmx-cot-card>header>span{width:38px;height:38px;display:grid;place-items:center;border:1px solid var(--line);border-radius:12px;background:var(--card)}.fmx-cot-card header strong{display:block;font-size:14px}.fmx-cot-card header small{display:block;color:var(--muted);font-size:8px}.fmx-cot-card header b{font-size:9px;padding:5px 7px;border-radius:999px}.fmx-cot-card header b.is-up{color:var(--green);background:var(--green-soft)}.fmx-cot-card header b.is-down{color:var(--red);background:var(--red-soft)}.fmx-cot-card header b.is-neutral{color:var(--muted);background:var(--soft)}.fmx-cot-main{margin-top:16px}.fmx-cot-main strong{display:block;font-size:25px;letter-spacing:-.04em}.fmx-cot-main span{display:block;color:var(--muted);font-size:9px}.fmx-cot-axis{position:relative;height:7px;margin:12px 0;border-radius:999px;background:var(--line);overflow:hidden}.fmx-cot-axis .is-zero{position:absolute;left:50%;top:0;bottom:0;width:1px;background:var(--text);opacity:.4}.fmx-cot-axis .is-positive,.fmx-cot-axis .is-negative{position:absolute;top:0;bottom:0}.fmx-cot-axis .is-positive{background:var(--green)}.fmx-cot-axis .is-negative{background:var(--red)}.fmx-cot-meta{display:grid;grid-template-columns:1fr 1fr;gap:8px}.fmx-cot-meta span{padding:9px;border:1px solid var(--line);border-radius:10px;background:var(--card)}.fmx-cot-meta small{display:block;color:var(--muted);font-size:8px}.fmx-cot-meta strong{display:block;margin-top:3px;font-size:10px}.fmx-cot-card p{margin:10px 0 0;color:var(--muted);font-size:8px;line-height:1.45}
        .fmx-risk-layout{display:grid;grid-template-columns:minmax(0,1.35fr) minmax(300px,.65fr);gap:12px}.fmx-risk-kpis{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:14px}.fmx-risk-kpis article{padding:12px;border:1px solid var(--line);border-radius:14px;background:var(--soft)}.fmx-risk-kpis small{display:block;color:var(--muted);font-size:8px}.fmx-risk-kpis strong{display:block;margin-top:3px;font-size:22px}.fmx-risk-kpis span{font-size:8px;color:var(--sub)}.fmx-risk-events{display:flex;flex-direction:column}.fmx-risk-events button{display:grid;grid-template-columns:84px minmax(0,1fr) 70px 18px;gap:10px;align-items:center;padding:10px 4px;border:0;border-top:1px solid var(--line);background:transparent;text-align:left;cursor:pointer}.fmx-risk-event-currency{display:flex;align-items:center;gap:6px}.fmx-risk-events strong{display:block;font-size:10px}.fmx-risk-events small{display:block;margin-top:2px;color:var(--muted);font-size:8px}.fmx-geo-score{display:flex;align-items:baseline;gap:4px}.fmx-geo-score strong{font-size:42px;letter-spacing:-.05em}.fmx-geo-score span{color:var(--muted);font-size:12px}.fmx-geo-score b{margin-left:auto;padding:5px 8px;border-radius:999px;background:var(--purple-soft);color:var(--purple);font-size:9px}.fmx-geo-risk-card>p{color:var(--sub);font-size:10px;line-height:1.55}.fmx-geo-headlines{display:grid;gap:7px;margin-top:12px}.fmx-geo-headlines article{padding:9px;border:1px solid var(--line);border-radius:10px;background:var(--soft)}.fmx-geo-headlines strong{display:block;font-size:9px;line-height:1.4}.fmx-geo-headlines small{display:block;margin-top:3px;color:var(--muted);font-size:8px}
        .fmx-playbook-card>header{display:flex;align-items:center;justify-content:space-between;gap:12px}.fmx-playbook-pair{display:inline-flex;align-items:center;gap:7px}.fmx-playbook-pair strong{font-size:16px}.fmx-playbook-card>header>b{font-size:18px;color:var(--purple)}.fmx-playbook-tags{display:flex;gap:6px;flex-wrap:wrap;margin-top:10px}.fmx-playbook-tags span{padding:4px 7px;border-radius:999px;background:var(--purple-soft);color:var(--purple);font-size:8px;font-weight:750}.fmx-playbook-card>p{margin:10px 0;color:var(--sub);font-size:10px;line-height:1.5}.fmx-playbook-columns{display:grid;grid-template-columns:1fr 1fr;gap:8px}.fmx-playbook-columns>div{padding:9px;border:1px solid var(--line);border-radius:11px;background:var(--soft)}.fmx-playbook-columns small{display:block;margin-bottom:5px;color:var(--muted);font-size:8px;text-transform:uppercase}.fmx-playbook-columns span{display:flex;gap:5px;margin-top:4px;color:var(--sub);font-size:8px;line-height:1.4}.fmx-playbook-columns svg{width:12px;flex:0 0 12px}.fmx-playbook-columns em{font-style:normal;color:var(--muted);font-size:8px}.fmx-playbook-card footer{display:flex;align-items:center;justify-content:space-between;margin-top:12px;padding-top:9px;border-top:1px solid var(--line);color:var(--muted);font-size:8px}.fmx-playbook-card footer svg{width:15px;color:var(--purple)}
        @media(max-width:980px){.fmx-regime-grid,.fmx-bank-detail-grid,.fmx-cot-grid,.fmx-playbook-grid,.fmx-risk-layout{grid-template-columns:1fr}.fmx-positioning-note{grid-template-columns:34px 1fr}.fmx-positioning-note>small{grid-column:2}.fmx-risk-kpis{grid-template-columns:repeat(3,1fr)}}
        @media(max-width:640px){.fmx-bank-metrics,.fmx-risk-kpis{grid-template-columns:1fr}.fmx-playbook-columns,.fmx-pillar-mini-grid{grid-template-columns:1fr}.fmx-risk-events button{grid-template-columns:72px minmax(0,1fr) 18px}.fmx-risk-events .fmx-impact{display:none}.fmx-positioning-note{grid-template-columns:1fr}.fmx-positioning-note>small{grid-column:auto}}

        /* ===== Premium header + readable intelligence modals ===== */
        .fmx-topbar-premium {
          grid-template-columns: minmax(0, 1.12fr) minmax(500px, .88fr);
          gap: 28px;
          padding: 30px 30px 28px;
          border-radius: 30px;
          border-color: rgba(99,78,210,.14);
          background:
            linear-gradient(122deg, rgba(255,255,255,.98) 0%, rgba(250,249,255,.97) 47%, rgba(244,246,255,.94) 100%),
            var(--bg-elevated);
          box-shadow:
            0 28px 70px -38px rgba(58,42,132,.38),
            inset 0 1px 0 rgba(255,255,255,.85);
        }
        .fmx-topbar-premium::before {
          width: 520px;
          height: 520px;
          right: -210px;
          top: -345px;
          background: radial-gradient(circle, rgba(115,87,239,.22), rgba(115,87,239,.065) 38%, rgba(115,87,239,0) 70%);
        }
        .fmx-topbar-premium::after {
          height: 2px;
          background: linear-gradient(90deg, transparent 3%, rgba(115,87,239,.55) 34%, rgba(45,112,232,.35) 63%, transparent 97%);
          opacity: .58;
        }
        html.dark .fmx-topbar-premium,
        body.dark .fmx-topbar-premium,
        .dark .fmx-topbar-premium,
        [data-theme="dark"] .fmx-topbar-premium {
          border-color: rgba(154,130,255,.14);
          background:
            linear-gradient(122deg, rgba(20,27,43,.985) 0%, rgba(18,25,41,.97) 52%, rgba(20,27,48,.96) 100%),
            var(--bg-elevated);
          box-shadow: 0 34px 90px -44px rgba(0,0,0,.82), inset 0 1px 0 rgba(255,255,255,.04);
        }

        .fmx-header-main { min-width: 0; align-self: stretch; display: flex; align-items: center; }
        .fmx-title-wrap { align-items: flex-start; gap: 19px; }
        .fmx-title-icon {
          position: relative;
          width: 62px;
          height: 62px;
          flex-basis: 62px;
          border-radius: 20px;
          background:
            linear-gradient(145deg, #5f45db 0%, #7758ef 46%, #967cf7 100%);
          box-shadow:
            0 18px 34px -17px rgba(87,59,207,.78),
            inset 0 1px 0 rgba(255,255,255,.32);
        }
        .fmx-title-icon > svg { width: 27px; height: 27px; }
        .fmx-title-icon > i {
          position: absolute;
          right: -5px;
          bottom: -5px;
          width: 23px;
          height: 23px;
          display: grid;
          place-items: center;
          border: 3px solid var(--card-solid);
          border-radius: 9px;
          background: var(--text);
          color: var(--card-solid);
          box-shadow: 0 6px 16px rgba(18,21,34,.16);
        }
        .fmx-title-icon > i svg { width: 11px; height: 11px; }
        .fmx-title-copy { min-width: 0; }
        .fmx-header-kicker { display: flex; align-items: center; gap: 9px; flex-wrap: wrap; margin-bottom: 7px; }
        .fmx-eyebrow { margin: 0; font-size: 10px; letter-spacing: .12em; }
        .fmx-eyebrow-dot {
          width: 7px;
          height: 7px;
          border-radius: 999px;
          background: var(--purple);
          box-shadow: 0 0 0 5px var(--purple-soft);
        }
        .fmx-terminal-badge {
          display: inline-flex;
          align-items: center;
          min-height: 22px;
          padding: 0 8px;
          border: 1px solid rgba(115,87,239,.14);
          border-radius: 999px;
          background: var(--purple-soft);
          color: var(--purple);
          font-size: 8px;
          font-weight: 900;
          letter-spacing: .12em;
        }
        .fmx-title-wrap h1 {
          display: flex;
          align-items: baseline;
          gap: 9px;
          margin: 0;
          font-family: Manrope, Inter, "SF Pro Display", "Segoe UI Variable Display", system-ui, sans-serif;
          font-size: clamp(30px, 2.45vw, 41px);
          font-weight: 790;
          letter-spacing: -.052em;
          line-height: 1;
        }
        .fmx-title-wrap h1 em {
          padding: 4px 7px 5px;
          border-radius: 8px;
          background: linear-gradient(135deg, var(--purple), var(--purple-2));
          color: #fff;
          font-size: .34em;
          font-style: normal;
          font-weight: 900;
          letter-spacing: .04em;
          text-transform: uppercase;
          transform: translateY(-3px);
          box-shadow: 0 6px 16px -9px rgba(95,69,219,.72);
        }
        .fmx-title-wrap p {
          max-width: 760px;
          margin: 10px 0 0;
          color: var(--sub);
          font-size: 13.5px;
          font-weight: 500;
          line-height: 1.65;
        }
        .fmx-header-capabilities {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 7px;
          margin-top: 15px;
        }
        .fmx-header-capabilities span {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 9px;
          border: 1px solid var(--line);
          border-radius: 999px;
          background: rgba(255,255,255,.56);
          color: var(--sub);
          font-size: 9px;
          font-weight: 720;
          line-height: 1;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.65);
        }
        .fmx-header-capabilities svg { color: var(--purple); width: 12px; height: 12px; }
        html.dark .fmx-header-capabilities span,
        body.dark .fmx-header-capabilities span,
        .dark .fmx-header-capabilities span,
        [data-theme="dark"] .fmx-header-capabilities span {
          background: rgba(255,255,255,.035);
          box-shadow: inset 0 1px 0 rgba(255,255,255,.025);
        }

        .fmx-header-console {
          min-width: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 12px;
          padding-left: 2px;
        }
        .fmx-header-console .fmx-actions { justify-content: flex-end; }
        .fmx-header-console .fmx-zone {
          min-width: 205px;
          display: grid;
          grid-template-columns: 16px auto minmax(90px,1fr) 14px;
          gap: 7px;
          padding: 0 11px;
        }
        .fmx-header-console .fmx-zone > span {
          color: var(--muted);
          font-size: 8px;
          font-weight: 850;
          letter-spacing: .06em;
          text-transform: uppercase;
        }
        .fmx-header-console .fmx-zone select {
          min-width: 0;
          padding: 0;
          font-size: 11px;
          font-weight: 750;
        }
        .fmx-header-console .fmx-zone svg:last-child { position: static; }
        .fmx-header-console .fmx-refresh { min-width: 116px; }

        .fmx-market-pulse {
          display: grid;
          grid-template-columns: repeat(2,minmax(0,1fr));
          gap: 8px;
        }
        .fmx-market-pulse article {
          min-width: 0;
          min-height: 58px;
          display: grid;
          grid-template-columns: 34px minmax(0,1fr);
          gap: 9px;
          align-items: center;
          padding: 10px 11px;
          border: 1px solid var(--line);
          border-radius: 15px;
          background: rgba(255,255,255,.58);
          box-shadow: inset 0 1px 0 rgba(255,255,255,.64), 0 9px 24px -19px rgba(28,34,58,.35);
        }
        html.dark .fmx-market-pulse article,
        body.dark .fmx-market-pulse article,
        .dark .fmx-market-pulse article,
        [data-theme="dark"] .fmx-market-pulse article { background: rgba(255,255,255,.035); box-shadow: inset 0 1px 0 rgba(255,255,255,.025); }
        .fmx-market-pulse article > svg {
          width: 30px;
          height: 30px;
          padding: 7px;
          border-radius: 10px;
          background: var(--purple-soft);
          color: var(--purple);
        }
        .fmx-pulse-flag {
          width: 34px;
          height: 34px;
          display: grid;
          place-items: center;
          border: 1px solid var(--line);
          border-radius: 11px;
          background: var(--card-solid);
          font-size: 19px;
          box-shadow: var(--shadow-sm);
        }
        .fmx-market-pulse small {
          display: block;
          color: var(--muted);
          font-size: 8px;
          font-weight: 850;
          letter-spacing: .065em;
          text-transform: uppercase;
        }
        .fmx-market-pulse strong {
          display: flex;
          align-items: baseline;
          gap: 6px;
          margin-top: 3px;
          color: var(--text);
          font-size: 13px;
          font-weight: 820;
          line-height: 1.1;
        }
        .fmx-market-pulse strong b { color: var(--purple); font-size: 11px; font-weight: 850; }
        .fmx-market-pulse .is-strong strong b { color: var(--green); }
        .fmx-market-pulse .is-weak strong b { color: var(--red); }
        .fmx-market-pulse .is-spread strong b { color: var(--muted); font-size: 9px; }

        .fmx-header-foot {
          display: flex;
          justify-content: flex-end;
          flex-wrap: wrap;
          gap: 7px 13px;
          color: var(--muted);
          font-size: 9px;
          font-weight: 680;
        }
        .fmx-header-foot span { display: inline-flex; align-items: center; gap: 5px; }
        .fmx-header-foot svg { color: var(--purple); width: 12px; height: 12px; }
        .fmx-header-foot strong { color: var(--text); font-weight: 850; }

        .fmx-modal-backdrop {
          --modal-surface: #ffffff;
          --modal-soft: #f6f7fb;
          --modal-soft-2: #eef1f8;
          --modal-text: #121522;
          --modal-sub: #566078;
          --modal-muted: #8791a6;
          --modal-line: rgba(25,34,58,.105);
          --modal-line-strong: rgba(25,34,58,.16);
          --modal-purple: #7256ec;
          --modal-purple-soft: rgba(114,86,236,.10);
          --modal-green: #0f966f;
          --modal-green-soft: rgba(15,150,111,.10);
          --modal-red: #d64b65;
          --modal-red-soft: rgba(214,75,101,.10);
          --modal-amber: #c97e13;
          --modal-amber-soft: rgba(201,126,19,.11);
          --modal-blue: #2c6fdf;
          --modal-blue-soft: rgba(44,111,223,.10);
          --text: var(--modal-text);
          --sub: var(--modal-sub);
          --muted: var(--modal-muted);
          --line: var(--modal-line);
          --line-strong: var(--modal-line-strong);
          --card: var(--modal-surface);
          --card-solid: var(--modal-surface);
          --soft: var(--modal-soft);
          --purple: var(--modal-purple);
          --purple-soft: var(--modal-purple-soft);
          --green: var(--modal-green);
          --green-soft: var(--modal-green-soft);
          --red: var(--modal-red);
          --red-soft: var(--modal-red-soft);
          --amber: var(--modal-amber);
          --amber-soft: var(--modal-amber-soft);
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: grid;
          place-items: center;
          padding: 24px;
          background: rgba(13,18,30,.36);
          backdrop-filter: blur(9px) saturate(.9);
          -webkit-backdrop-filter: blur(9px) saturate(.9);
        }
        html.dark .fmx-modal-backdrop,
        body.dark .fmx-modal-backdrop,
        .dark .fmx-modal-backdrop,
        [data-theme="dark"] .fmx-modal-backdrop {
          --modal-surface: #171e2c;
          --modal-soft: #1c2536;
          --modal-soft-2: #222c3f;
          --modal-text: #f7f8fc;
          --modal-sub: #c6cede;
          --modal-muted: #93a0b5;
          --modal-line: rgba(255,255,255,.095);
          --modal-line-strong: rgba(255,255,255,.16);
          --modal-purple: #a28cff;
          --modal-purple-soft: rgba(162,140,255,.13);
          --modal-green: #4ad7a4;
          --modal-green-soft: rgba(74,215,164,.12);
          --modal-red: #ff788d;
          --modal-red-soft: rgba(255,120,141,.12);
          --modal-amber: #ffc06a;
          --modal-amber-soft: rgba(255,192,106,.12);
          --modal-blue: #76adff;
          --modal-blue-soft: rgba(118,173,255,.12);
          background: rgba(2,6,14,.52);
        }
        .fmx-modal {
          width: min(1140px, calc(100vw - 40px));
          max-height: min(92vh, 940px);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          border: 1px solid var(--modal-line-strong);
          border-radius: 26px;
          background: var(--modal-surface);
          color: var(--modal-text);
          box-shadow: 0 34px 100px -30px rgba(8,12,24,.52), 0 8px 30px rgba(8,12,24,.12);
        }
        .fmx-modal-header { position: relative; display: flex; justify-content: space-between; align-items: flex-start; gap: 18px; padding: 22px 24px 19px; border-bottom: 1px solid var(--modal-line); background: linear-gradient(180deg, var(--modal-surface), var(--modal-soft)); }
        .fmx-modal-header::after { content: ""; position: absolute; inset: auto 0 0; height: 1px; background: linear-gradient(90deg, transparent, var(--modal-purple), transparent); opacity: .34; }
        .fmx-modal-heading { min-width: 0; }
        .fmx-modal-eyebrow { display: inline-flex; align-items: center; gap: 6px; margin-bottom: 7px; color: var(--modal-purple); font-size: 10px; font-weight: 850; letter-spacing: .085em; text-transform: uppercase; }
        .fmx-modal-eyebrow svg { width: 12px; height: 12px; }
        .fmx-modal h3 { margin: 0; color: var(--modal-text); font-size: clamp(21px, 2vw, 28px); font-weight: 820; letter-spacing: -.035em; line-height: 1.08; }
        .fmx-modal-header p { max-width: 820px; margin: 8px 0 0; color: var(--modal-sub); font-size: 13px; font-weight: 500; line-height: 1.58; }
        .fmx-modal-close { width: 40px; height: 40px; display: grid; place-items: center; flex: 0 0 40px; border: 1px solid var(--modal-line); border-radius: 13px; background: var(--modal-surface); color: var(--modal-sub); cursor: pointer; box-shadow: 0 5px 18px rgba(12,18,32,.06); }
        .fmx-modal-close:hover { border-color: color-mix(in srgb, var(--modal-purple) 28%, var(--modal-line)); color: var(--modal-purple); transform: translateY(-1px); }
        .fmx-modal-body { overflow: auto; padding: 22px 24px 26px; background: var(--modal-surface); color: var(--modal-text); scrollbar-width: thin; scrollbar-color: var(--modal-line-strong) transparent; }

        .fmx-modal-hero { display: grid; grid-template-columns: 48px minmax(0,1fr) auto; gap: 14px; align-items: center; margin-bottom: 15px; padding: 16px; border: 1px solid var(--modal-line); border-radius: 17px; background: var(--modal-soft); }
        .fmx-modal-hero.is-up { border-color: color-mix(in srgb, var(--modal-green) 24%, var(--modal-line)); background: linear-gradient(135deg, var(--modal-green-soft), var(--modal-soft)); }
        .fmx-modal-hero.is-down { border-color: color-mix(in srgb, var(--modal-red) 24%, var(--modal-line)); background: linear-gradient(135deg, var(--modal-red-soft), var(--modal-soft)); }
        .fmx-modal-hero.is-warning, .fmx-modal-hero.is-pending { border-color: color-mix(in srgb, var(--modal-amber) 24%, var(--modal-line)); background: linear-gradient(135deg, var(--modal-amber-soft), var(--modal-soft)); }
        .fmx-modal-hero.is-neutral { background: linear-gradient(135deg, var(--modal-purple-soft), var(--modal-soft)); }
        .fmx-modal-hero-icon { width: 48px; height: 48px; display: grid; place-items: center; border-radius: 15px; background: var(--modal-surface); color: var(--modal-purple); border: 1px solid var(--modal-line); box-shadow: 0 8px 22px rgba(11,19,38,.07); }
        .fmx-modal-hero.is-up .fmx-modal-hero-icon { color: var(--modal-green); }
        .fmx-modal-hero.is-down .fmx-modal-hero-icon { color: var(--modal-red); }
        .fmx-modal-hero.is-warning .fmx-modal-hero-icon, .fmx-modal-hero.is-pending .fmx-modal-hero-icon { color: var(--modal-amber); }
        .fmx-modal-hero-copy > span { color: var(--modal-muted); font-size: 9px; font-weight: 820; letter-spacing: .075em; text-transform: uppercase; }
        .fmx-modal-hero-copy h4 { margin: 3px 0 0; color: var(--modal-text); font-size: 17px; font-weight: 820; letter-spacing: -.02em; line-height: 1.25; }
        .fmx-modal-hero-copy p { margin: 5px 0 0; color: var(--modal-sub); font-size: 11px; line-height: 1.6; }
        .fmx-decision-pill { max-width: 245px; padding: 7px 10px; border-radius: 999px; background: var(--modal-purple-soft); color: var(--modal-purple); font-size: 9px; font-weight: 820; line-height: 1.35; text-align: center; }
        .fmx-decision-pill.is-up { background: var(--modal-green-soft); color: var(--modal-green); }
        .fmx-decision-pill.is-down { background: var(--modal-red-soft); color: var(--modal-red); }
        .fmx-decision-pill.is-warning, .fmx-decision-pill.is-pending { background: var(--modal-amber-soft); color: var(--modal-amber); }

        .fmx-currency-modal-hero {
          --currency-modal-accent: var(--modal-purple);
          --currency-modal-soft: var(--modal-purple-soft);
          display: grid;
          grid-template-columns: 82px minmax(0,1fr) 118px;
          gap: 18px;
          align-items: center;
          margin-bottom: 13px;
          padding: 19px;
          border: 1px solid var(--modal-line);
          border-radius: 20px;
          background:
            linear-gradient(118deg, var(--currency-modal-soft), transparent 48%),
            var(--modal-soft);
          overflow: hidden;
        }
        .fmx-currency-modal-hero.is-up { --currency-modal-accent: var(--modal-green); --currency-modal-soft: var(--modal-green-soft); }
        .fmx-currency-modal-hero.is-down { --currency-modal-accent: var(--modal-red); --currency-modal-soft: var(--modal-red-soft); }
        .fmx-currency-modal-hero.is-neutral { --currency-modal-accent: var(--modal-purple); --currency-modal-soft: var(--modal-purple-soft); }

        .fmx-currency-modal-flag {
          width: 82px;
          height: 82px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          border: 1px solid color-mix(in srgb, var(--currency-modal-accent) 18%, var(--modal-line));
          border-radius: 22px;
          background: var(--modal-surface);
          box-shadow: 0 14px 30px -22px rgba(10,18,34,.35), inset 0 1px 0 rgba(255,255,255,.5);
        }
        .fmx-currency-modal-flag > span { display:flex; align-items:center; justify-content:center; line-height:1; }
        .fmx-currency-modal-flag > small {
          margin-top: 7px;
          color: var(--currency-modal-accent);
          font-size: 8px;
          font-weight: 900;
          letter-spacing: .08em;
          text-transform: uppercase;
        }

        .fmx-currency-modal-copy { min-width: 0; }
        .fmx-currency-modal-overline {
          display: block;
          color: var(--modal-muted);
          font-size: 9px;
          font-weight: 850;
          letter-spacing: .075em;
          text-transform: uppercase;
        }
        .fmx-currency-modal-copy h4 {
          display: flex;
          align-items: baseline;
          gap: 9px;
          margin: 3px 0 0;
          color: var(--modal-text);
          font-size: 25px;
          font-weight: 880;
          letter-spacing: -.04em;
          line-height: 1;
        }
        .fmx-currency-modal-copy h4 em {
          color: var(--modal-sub);
          font-size: 12px;
          font-style: normal;
          font-weight: 670;
          letter-spacing: 0;
        }
        .fmx-currency-modal-copy p {
          max-width: 700px;
          margin: 8px 0 0;
          color: var(--modal-sub);
          font-size: 11.5px;
          font-weight: 510;
          line-height: 1.62;
        }
        .fmx-currency-modal-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
        .fmx-currency-modal-tags span {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          min-height: 26px;
          padding: 0 8px;
          border: 1px solid var(--modal-line);
          border-radius: 999px;
          background: var(--modal-surface);
          color: var(--modal-sub);
          font-size: 8.5px;
          font-weight: 760;
        }
        .fmx-currency-modal-tags span.is-up { color: var(--modal-green); background: var(--modal-green-soft); border-color: color-mix(in srgb, var(--modal-green) 18%, var(--modal-line)); }
        .fmx-currency-modal-tags span.is-down { color: var(--modal-red); background: var(--modal-red-soft); border-color: color-mix(in srgb, var(--modal-red) 18%, var(--modal-line)); }
        .fmx-currency-modal-tags span.is-neutral { color: var(--modal-purple); background: var(--modal-purple-soft); }
        .fmx-currency-modal-tags svg { width: 12px; height: 12px; }

        .fmx-currency-modal-score {
          --score-accent: var(--currency-modal-accent);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 7px;
          justify-self: end;
        }
        .fmx-currency-modal-score > div {
          position: relative;
          width: 88px;
          height: 88px;
          display: flex;
          align-items: baseline;
          justify-content: center;
          padding-top: 31px;
          border-radius: 999px;
          background: conic-gradient(var(--score-accent) var(--score-angle), var(--modal-line) 0);
          box-shadow: 0 12px 26px -20px color-mix(in srgb, var(--score-accent) 55%, transparent);
        }
        .fmx-currency-modal-score > div::after {
          content: "";
          position: absolute;
          inset: 7px;
          border: 1px solid var(--modal-line);
          border-radius: inherit;
          background: var(--modal-surface);
        }
        .fmx-currency-modal-score strong,
        .fmx-currency-modal-score small { position: relative; z-index: 1; }
        .fmx-currency-modal-score strong {
          color: var(--modal-text);
          font-size: 22px;
          font-weight: 900;
          letter-spacing: -.045em;
          line-height: 1;
        }
        .fmx-currency-modal-score small { margin-left: 2px; color: var(--modal-muted); font-size: 8px; font-weight: 760; }
        .fmx-currency-modal-score > span {
          color: var(--modal-muted);
          font-size: 8px;
          font-weight: 850;
          letter-spacing: .055em;
          text-transform: uppercase;
        }

        .fmx-currency-components {
          display: grid;
          grid-template-columns: repeat(3,minmax(0,1fr));
          gap: 9px;
          margin-bottom: 13px;
        }
        .fmx-currency-components article {
          padding: 13px;
          border: 1px solid var(--modal-line);
          border-radius: 15px;
          background: var(--modal-soft);
        }
        .fmx-currency-components article.is-up { border-color: color-mix(in srgb, var(--modal-green) 20%, var(--modal-line)); background: linear-gradient(135deg, var(--modal-green-soft), var(--modal-soft)); }
        .fmx-currency-components article.is-down { border-color: color-mix(in srgb, var(--modal-red) 20%, var(--modal-line)); background: linear-gradient(135deg, var(--modal-red-soft), var(--modal-soft)); }
        .fmx-currency-components article > div { display: flex; align-items: center; gap: 6px; color: var(--modal-muted); }
        .fmx-currency-components article > div svg { color: var(--modal-purple); width: 14px; height: 14px; }
        .fmx-currency-components article > div span {
          font-size: 8.5px;
          font-weight: 850;
          letter-spacing: .055em;
          text-transform: uppercase;
        }
        .fmx-currency-components strong {
          display: block;
          margin-top: 5px;
          color: var(--modal-text);
          font-size: 18px;
          font-weight: 860;
          letter-spacing: -.025em;
        }
        .fmx-currency-components article.is-up strong { color: var(--modal-green); }
        .fmx-currency-components article.is-down strong { color: var(--modal-red); }
        .fmx-currency-components p {
          margin: 5px 0 0;
          color: var(--modal-sub);
          font-size: 9.5px;
          line-height: 1.5;
        }

        .fmx-modal-metrics { display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 9px; margin-bottom: 15px; }
        .fmx-modal-metric { min-width: 0; padding: 12px 13px; border: 1px solid var(--modal-line); border-radius: 14px; background: var(--modal-soft); }
        .fmx-modal-metric small { display: block; color: var(--modal-muted); font-size: 9px; font-weight: 820; letter-spacing: .06em; text-transform: uppercase; }
        .fmx-modal-metric strong { display: block; margin-top: 5px; color: var(--modal-text); font-size: 17px; font-weight: 840; letter-spacing: -.015em; line-height: 1.2; }
        .fmx-modal-metric span { display: block; margin-top: 5px; color: var(--modal-sub); font-size: 10.5px; line-height: 1.48; }
        .fmx-modal-metric.is-up strong { color: var(--modal-green); }
        .fmx-modal-metric.is-down strong { color: var(--modal-red); }
        .fmx-modal-metric.is-warning strong { color: var(--modal-amber); }

        .fmx-pair-compare { display: grid; grid-template-columns: minmax(0,1fr) 42px minmax(0,1fr); gap: 10px; align-items: stretch; margin-bottom: 15px; }
        .fmx-pair-compare > article { padding: 14px; border: 1px solid var(--modal-line); border-radius: 15px; background: var(--modal-surface); box-shadow: 0 8px 24px rgba(13,19,34,.04); }
        .fmx-compare-kicker { color: var(--modal-muted); font-size: 8px; font-weight: 820; letter-spacing: .065em; text-transform: uppercase; }
        .fmx-compare-score { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-top: 5px; }
        .fmx-compare-score strong { display: inline-flex; align-items: center; gap: 6px; font-size: 18px; }
        .fmx-compare-score strong .fmx-inline-flag { font-size: 17px; }
        .fmx-compare-score b { color: var(--modal-purple); font-size: 20px; }
        .fmx-pair-compare article small { color: var(--modal-muted); font-size: 9px; }
        .fmx-pair-compare article p { margin: 7px 0 0; color: var(--modal-sub); font-size: 10px; line-height: 1.55; }
        .fmx-vs-badge { width: 42px; height: 42px; align-self: center; display: grid; place-items: center; border: 1px solid var(--modal-line); border-radius: 999px; background: var(--modal-soft); color: var(--modal-purple); font-size: 9px; font-weight: 900; }

        .fmx-insight-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 10px; }
        .fmx-insight-block { min-width: 0; display: grid; grid-template-columns: 34px minmax(0,1fr); gap: 10px; padding: 13px; border: 1px solid var(--modal-line); border-radius: 15px; background: var(--modal-soft); }
        .fmx-insight-block.is-up { border-color: color-mix(in srgb, var(--modal-green) 20%, var(--modal-line)); background: linear-gradient(135deg, var(--modal-green-soft), var(--modal-soft)); }
        .fmx-insight-block.is-down { border-color: color-mix(in srgb, var(--modal-red) 20%, var(--modal-line)); background: linear-gradient(135deg, var(--modal-red-soft), var(--modal-soft)); }
        .fmx-insight-block.is-warning, .fmx-insight-block.is-pending { border-color: color-mix(in srgb, var(--modal-amber) 20%, var(--modal-line)); background: linear-gradient(135deg, var(--modal-amber-soft), var(--modal-soft)); }
        .fmx-insight-block.is-info { border-color: color-mix(in srgb, var(--modal-blue) 20%, var(--modal-line)); background: linear-gradient(135deg, var(--modal-blue-soft), var(--modal-soft)); }
        .fmx-insight-icon { width: 34px; height: 34px; display: grid; place-items: center; border-radius: 11px; background: var(--modal-surface); color: var(--modal-purple); border: 1px solid var(--modal-line); }
        .fmx-insight-block.is-up .fmx-insight-icon { color: var(--modal-green); }
        .fmx-insight-block.is-down .fmx-insight-icon { color: var(--modal-red); }
        .fmx-insight-block.is-warning .fmx-insight-icon, .fmx-insight-block.is-pending .fmx-insight-icon { color: var(--modal-amber); }
        .fmx-insight-block.is-info .fmx-insight-icon { color: var(--modal-blue); }
        .fmx-insight-eyebrow { display: block; color: var(--modal-muted); font-size: 9px; font-weight: 820; letter-spacing: .065em; text-transform: uppercase; }
        .fmx-insight-content h4 { margin: 3px 0 0; color: var(--modal-text); font-size: 13.5px; font-weight: 820; letter-spacing: -.01em; line-height: 1.35; }
        .fmx-insight-content p { margin: 6px 0 0; color: var(--modal-sub); font-size: 11.5px; line-height: 1.62; }

        .fmx-playbook-panel, .fmx-breakdown-panel, .fmx-driver-section, .fmx-watch-panel, .fmx-scenario-panel, .fmx-event-conclusion, .fmx-event-checklist { margin-top: 14px; padding: 15px; border: 1px solid var(--modal-line); border-radius: 16px; background: var(--modal-surface); box-shadow: 0 8px 28px rgba(12,18,34,.035); }
        .fmx-playbook-head, .fmx-subsection-title { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 11px; }
        .fmx-playbook-head span, .fmx-subsection-title span { display: block; color: var(--modal-purple); font-size: 9px; font-weight: 850; letter-spacing: .07em; text-transform: uppercase; }
        .fmx-playbook-head h4, .fmx-subsection-title h4 { margin: 3px 0 0; color: var(--modal-text); font-size: 14.5px; font-weight: 830; letter-spacing: -.015em; }
        .fmx-playbook-head > b { padding: 6px 9px; border-radius: 999px; background: var(--modal-purple-soft); color: var(--modal-purple); font-size: 9px; white-space: nowrap; }
        .fmx-subsection-title > p { max-width: 430px; margin: 0; color: var(--modal-muted); font-size: 10.5px; line-height: 1.55; text-align: right; }
        .fmx-playbook-columns { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .fmx-playbook-columns > div { padding: 12px; border-radius: 13px; background: var(--modal-soft); }
        .fmx-playbook-columns h5 { display: flex; align-items: center; gap: 6px; margin: 0 0 7px; color: var(--modal-text); font-size: 10px; }
        .fmx-playbook-columns h5 svg { color: var(--modal-purple); }
        .fmx-playbook-columns ul { margin: 0; padding-left: 17px; }
        .fmx-playbook-columns li, .fmx-playbook-columns p { margin: 0; color: var(--modal-sub); font-size: 9px; line-height: 1.55; }
        .fmx-playbook-columns li + li { margin-top: 4px; }
        .fmx-news-risk-strip { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 10px; }
        .fmx-news-risk-strip > span { display: inline-flex; align-items: center; gap: 5px; padding: 6px 8px; border-radius: 10px; background: var(--modal-amber-soft); color: var(--modal-sub); font-size: 8px; }
        .fmx-news-risk-strip svg, .fmx-news-risk-strip strong { color: var(--modal-amber); }
        .fmx-news-risk-strip small { color: var(--modal-muted); }

        .fmx-breakdown-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 8px; }
        .fmx-breakdown-grid article { padding: 10px; border-radius: 12px; background: var(--modal-soft); }
        .fmx-breakdown-grid article > div:first-child { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
        .fmx-breakdown-grid span { color: var(--modal-sub); font-size: 10px; font-weight: 680; }
        .fmx-breakdown-grid strong { color: var(--modal-text); font-size: 13px; font-weight: 850; }
        .fmx-breakdown-track { height: 5px; margin-top: 7px; overflow: hidden; border-radius: 999px; background: var(--modal-line); }
        .fmx-breakdown-track i { display: block; height: 100%; border-radius: inherit; background: linear-gradient(90deg, var(--modal-purple), #a78bfa); }
        .fmx-currency-breakdown .fmx-breakdown-grid article {
          position: relative;
          overflow: hidden;
          border: 1px solid var(--modal-line);
          background: var(--modal-soft);
        }
        .fmx-currency-breakdown .fmx-breakdown-grid article::before {
          content: "";
          position: absolute;
          inset: 0 0 auto;
          height: 2px;
          background: var(--modal-purple);
          opacity: .55;
        }
        .fmx-currency-breakdown .fmx-breakdown-grid article.is-up::before { background: var(--modal-green); }
        .fmx-currency-breakdown .fmx-breakdown-grid article.is-down::before { background: var(--modal-red); }
        .fmx-currency-breakdown .fmx-breakdown-grid article > small {
          display: block;
          margin-top: 6px;
          color: var(--modal-muted);
          font-size: 8.5px;
          font-weight: 720;
        }
        .fmx-currency-breakdown .fmx-breakdown-grid article.is-up > small { color: var(--modal-green); }
        .fmx-currency-breakdown .fmx-breakdown-grid article.is-down > small { color: var(--modal-red); }
        .fmx-currency-breakdown .fmx-breakdown-track { position: relative; height: 6px; overflow: hidden; }
        .fmx-currency-breakdown .fmx-breakdown-track i { background: linear-gradient(90deg, var(--modal-purple), #a78bfa); }
        .fmx-currency-breakdown .fmx-breakdown-grid article.is-up .fmx-breakdown-track i { background: linear-gradient(90deg, color-mix(in srgb, var(--modal-green) 68%, #fff), var(--modal-green)); }
        .fmx-currency-breakdown .fmx-breakdown-grid article.is-down .fmx-breakdown-track i { background: linear-gradient(90deg, color-mix(in srgb, var(--modal-red) 68%, #fff), var(--modal-red)); }

        .fmx-component-strip { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 10px; }
        .fmx-component-strip span { padding: 5px 8px; border: 1px solid var(--modal-line); border-radius: 999px; color: var(--modal-sub); font-size: 8px; background: var(--modal-soft); }
        .fmx-component-strip strong { color: var(--modal-text); }

        .fmx-driver-list { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 8px; }
        .fmx-driver-list article { padding: 11px; border: 1px solid var(--modal-line); border-radius: 13px; background: var(--modal-soft); }
        .fmx-driver-top { display: flex; justify-content: space-between; gap: 10px; }
        .fmx-driver-top span { color: var(--modal-purple); font-size: 8px; font-weight: 800; }
        .fmx-driver-top b { font-size: 9px; }
        .fmx-driver-top b.is-up { color: var(--modal-green); }
        .fmx-driver-top b.is-down { color: var(--modal-red); }
        .fmx-driver-top b.is-neutral { color: var(--modal-muted); }
        .fmx-driver-list h5 { margin: 6px 0 0; color: var(--modal-text); font-size: 12px; font-weight: 820; line-height: 1.35; }
        .fmx-driver-list p { margin: 5px 0 0; color: var(--modal-sub); font-size: 10.5px; line-height: 1.58; }
        .fmx-driver-list small { display: inline-block; margin-top: 6px; color: var(--modal-muted); font-size: 8px; }
        .fmx-driver-list article.is-up { border-color: color-mix(in srgb, var(--modal-green) 18%, var(--modal-line)); }
        .fmx-driver-list article.is-down { border-color: color-mix(in srgb, var(--modal-red) 18%, var(--modal-line)); }
        .fmx-driver-foot { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 8px; padding-top: 7px; border-top: 1px solid var(--modal-line); }
        .fmx-driver-foot small { margin: 0; }
        .fmx-driver-foot em { color: var(--modal-muted); font-size: 8px; font-style: normal; font-weight: 760; }
        .fmx-driver-list article.is-up .fmx-driver-foot em { color: var(--modal-green); }
        .fmx-driver-list article.is-down .fmx-driver-foot em { color: var(--modal-red); }
        .fmx-watch-panel-premium { background: linear-gradient(135deg, var(--modal-purple-soft), var(--modal-surface)); }
        .fmx-watch-panel-premium .fmx-watch-chips span { display: inline-flex; align-items: center; gap: 5px; font-size: 9px; padding: 7px 9px; }
        .fmx-watch-panel-premium .fmx-watch-chips svg { width: 11px; height: 11px; }


        .fmx-watch-panel > div:first-child { display: flex; align-items: center; gap: 9px; }
        .fmx-watch-panel > div:first-child > svg { color: var(--modal-purple); }
        .fmx-watch-panel small { display: block; color: var(--modal-muted); font-size: 8px; text-transform: uppercase; letter-spacing: .06em; }
        .fmx-watch-panel strong { display: block; margin-top: 2px; color: var(--modal-text); font-size: 11px; }
        .fmx-watch-chips, .fmx-affected-assets { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 9px; }
        .fmx-watch-chips span, .fmx-affected-assets span { padding: 5px 8px; border-radius: 999px; background: var(--modal-purple-soft); color: var(--modal-purple); font-size: 8px; font-weight: 760; }

        .fmx-event-values { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 9px; margin-bottom: 10px; }
        .fmx-event-values article { padding: 13px; border: 1px solid var(--modal-line); border-radius: 14px; background: var(--modal-soft); }
        .fmx-event-values article.is-up { border-color: color-mix(in srgb, var(--modal-green) 24%, var(--modal-line)); background: var(--modal-green-soft); }
        .fmx-event-values article.is-down { border-color: color-mix(in srgb, var(--modal-red) 24%, var(--modal-line)); background: var(--modal-red-soft); }
        .fmx-event-values article.is-pending { border-color: color-mix(in srgb, var(--modal-amber) 24%, var(--modal-line)); background: var(--modal-amber-soft); }
        .fmx-event-values span { display: block; color: var(--modal-muted); font-size: 8px; font-weight: 820; text-transform: uppercase; letter-spacing: .06em; }
        .fmx-event-values strong { display: block; margin-top: 4px; color: var(--modal-text); font-size: 18px; font-weight: 850; }
        .fmx-event-values p { margin: 5px 0 0; color: var(--modal-sub); font-size: 9px; line-height: 1.5; }
        .fmx-event-conclusion-grid, .fmx-scenario-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 9px; }
        .fmx-event-conclusion-grid { grid-template-columns: repeat(3,minmax(0,1fr)); }
        .fmx-checklist-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 7px; }
        .fmx-checklist-grid span { display: flex; align-items: flex-start; gap: 7px; padding: 9px 10px; border-radius: 11px; background: var(--modal-soft); color: var(--modal-sub); font-size: 9px; line-height: 1.5; }
        .fmx-checklist-grid svg { flex: 0 0 14px; margin-top: 1px; color: var(--modal-green); }
        .fmx-affected-assets { align-items: center; margin-top: 10px; padding-top: 10px; border-top: 1px solid var(--modal-line); }
        .fmx-affected-assets small { margin-right: 3px; color: var(--modal-muted); font-size: 8px; font-weight: 800; text-transform: uppercase; }

        @media (max-width: 1120px) {
          .fmx-topbar-premium { grid-template-columns: 1fr; gap: 20px; }
          .fmx-header-console { padding-left: 81px; }
          .fmx-header-console .fmx-actions { justify-content: flex-start; }
          .fmx-header-foot { justify-content: flex-start; }
          .fmx-market-pulse { grid-template-columns: repeat(4,minmax(0,1fr)); }
          .fmx-modal-metrics { grid-template-columns: repeat(2,minmax(0,1fr)); }
          .fmx-event-conclusion-grid { grid-template-columns: 1fr; }
          .fmx-currency-modal-hero { grid-template-columns: 72px minmax(0,1fr) 104px; }
          .fmx-currency-modal-flag { width: 72px; height: 72px; border-radius: 19px; }
          .fmx-currency-modal-flag > span { font-size: 35px; }
          .fmx-currency-modal-score > div { width: 78px; height: 78px; padding-top: 27px; }
          .fmx-currency-components { grid-template-columns: repeat(3,minmax(0,1fr)); }
        }
        @media (max-width: 760px) {
          .fmx-topbar-premium { padding: 18px; border-radius: 22px; }
          .fmx-title-wrap { gap: 13px; }
          .fmx-topbar-premium .fmx-title-icon { width: 50px; height: 50px; flex-basis: 50px; border-radius: 16px; }
          .fmx-topbar-premium .fmx-title-icon > svg { width: 22px; height: 22px; }
          .fmx-topbar-premium .fmx-title-icon > i { width: 20px; height: 20px; right: -4px; bottom: -4px; border-width: 2px; }
          .fmx-title-wrap h1 { font-size: 25px; }
          .fmx-title-wrap h1 em { font-size: 8px; transform: none; }
          .fmx-title-wrap p { font-size: 11.5px; line-height: 1.55; }
          .fmx-header-capabilities { display: none; }
          .fmx-header-console { padding-left: 0; }
          .fmx-header-console .fmx-actions { display: grid; grid-template-columns: minmax(0,1fr) auto; }
          .fmx-header-console .fmx-zone { min-width: 0; grid-template-columns: 16px minmax(0,1fr) 14px; }
          .fmx-header-console .fmx-zone > span { display: none; }
          .fmx-header-console .fmx-refresh { min-width: 0; padding-inline: 12px; }
          .fmx-market-pulse { grid-template-columns: repeat(2,minmax(0,1fr)); }
          .fmx-market-pulse article { min-height: 55px; }
          .fmx-header-foot { gap: 6px 10px; }
          .fmx-header-foot span:nth-child(2), .fmx-header-foot span:nth-child(3) { display: none; }

          .fmx-strength-section { padding: 16px; }
          .fmx-strength-section .fmx-section-head { flex-direction: column; }
          .fmx-strength-overview { grid-template-columns: 1fr; padding: 13px; }
          .fmx-strength-spread { min-height: 62px; flex-direction: row; gap: 5px; }
          .fmx-strength-spread strong { font-size: 19px; }
          .fmx-strength-overview-copy > strong { flex-wrap: wrap; font-size: 15px; }
          .fmx-currency-strip { grid-template-columns: repeat(2,minmax(0,1fr)); }
          .fmx-currency-row { min-height: 154px; padding: 12px; }

          .fmx-modal-backdrop { padding: 8px; align-items: end; }
          .fmx-modal { width: 100%; max-height: 94vh; border-radius: 22px 22px 12px 12px; }
          .fmx-modal-header { padding: 17px 16px 15px; }
          .fmx-modal-body { padding: 15px 14px 20px; }
          .fmx-modal-hero { grid-template-columns: 42px minmax(0,1fr); }
          .fmx-modal-hero-icon { width: 42px; height: 42px; }
          .fmx-decision-pill { grid-column: 1/3; max-width: none; justify-self: start; text-align: left; }
          .fmx-modal-metrics, .fmx-insight-grid, .fmx-playbook-columns, .fmx-breakdown-grid, .fmx-driver-list, .fmx-event-values, .fmx-event-conclusion-grid, .fmx-scenario-grid, .fmx-checklist-grid { grid-template-columns: 1fr; }
          .fmx-pair-compare { grid-template-columns: 1fr; }
          .fmx-vs-badge { justify-self: center; }
          .fmx-playbook-head, .fmx-subsection-title { flex-direction: column; }
          .fmx-subsection-title > p { text-align: left; }

          .fmx-currency-modal-hero { grid-template-columns: 58px minmax(0,1fr); gap: 12px; padding: 14px; }
          .fmx-currency-modal-flag { width: 58px; height: 58px; border-radius: 16px; }
          .fmx-currency-modal-flag > span { font-size: 29px; }
          .fmx-currency-modal-flag > small { margin-top: 4px; font-size: 7px; }
          .fmx-currency-modal-copy h4 { flex-direction: column; align-items: flex-start; gap: 3px; font-size: 21px; }
          .fmx-currency-modal-copy p { font-size: 10.5px; }
          .fmx-currency-modal-tags { margin-top: 8px; }
          .fmx-currency-modal-score { grid-column: 1/3; width: 100%; flex-direction: row; justify-content: flex-start; padding-top: 4px; border-top: 1px solid var(--modal-line); }
          .fmx-currency-modal-score > div { width: 64px; height: 64px; padding-top: 22px; }
          .fmx-currency-modal-score strong { font-size: 18px; }
          .fmx-currency-components { grid-template-columns: 1fr; }
        }
        @media (max-width: 460px) {
          .fmx-currency-strip { grid-template-columns: 1fr; }
          .fmx-market-pulse { grid-template-columns: 1fr 1fr; }
          .fmx-market-pulse article:nth-child(4) { grid-column: 1/3; }
          .fmx-header-kicker { align-items: flex-start; flex-direction: column; gap: 6px; }
          .fmx-title-wrap h1 { flex-wrap: wrap; }
        }
        @media (prefers-reduced-motion: reduce) {
          .fmx-page *, .fmx-page *::before, .fmx-page *::after { scroll-behavior: auto !important; animation-duration: .01ms !important; animation-iteration-count: 1 !important; transition-duration: .01ms !important; }
        }

        /* Header simple, stylé et plus propre */
        .fmx-topbar-clean {
          grid-template-columns: minmax(0,1fr) auto;
          gap: 30px;
          padding: 24px 26px;
          border-radius: 24px;
          background:
            linear-gradient(118deg, rgba(255,255,255,.94), rgba(248,247,255,.90) 62%, rgba(242,246,255,.90)),
            var(--bg-elevated);
        }
        html.dark .fmx-topbar-clean,
        body.dark .fmx-topbar-clean,
        .dark .fmx-topbar-clean,
        [data-theme="dark"] .fmx-topbar-clean {
          background:
            linear-gradient(118deg, rgba(19,26,41,.97), rgba(18,24,39,.96) 62%, rgba(19,27,47,.95)),
            var(--bg-elevated);
        }
        .fmx-topbar-clean::before {
          width: 400px;
          height: 400px;
          right: -165px;
          top: -280px;
          opacity: .8;
        }
        .fmx-title-icon-clean {
          width: 56px;
          height: 56px;
          flex-basis: 56px;
          border-radius: 18px;
          box-shadow: 0 16px 34px -18px rgba(87,59,207,.72), inset 0 1px 0 rgba(255,255,255,.30);
        }
        .fmx-title-icon-clean > svg { width: 25px; height: 25px; }
        .fmx-header-kicker-clean { margin-bottom: 8px; gap: 9px; }
        .fmx-brand-word {
          color: var(--purple);
          font-size: 10px;
          font-weight: 900;
          letter-spacing: .15em;
        }
        .fmx-brand-separator {
          width: 18px;
          height: 1px;
          background: linear-gradient(90deg, var(--purple), transparent);
        }
        .fmx-module-word {
          color: var(--muted);
          font-size: 10px;
          font-weight: 760;
          letter-spacing: .055em;
          text-transform: uppercase;
        }
        .fmx-topbar-clean .fmx-title-wrap h1 {
          display: block;
          font-size: clamp(29px, 2.15vw, 38px);
          font-weight: 780;
          letter-spacing: -.046em;
          line-height: 1.03;
        }
        .fmx-topbar-clean .fmx-title-wrap p {
          max-width: 780px;
          margin-top: 9px;
          font-size: 13px;
          line-height: 1.6;
        }
        .fmx-header-glance {
          display: flex;
          align-items: stretch;
          flex-wrap: wrap;
          gap: 0;
          margin-top: 16px;
          width: fit-content;
          max-width: 100%;
          overflow: hidden;
          border: 1px solid var(--line);
          border-radius: 13px;
          background: rgba(255,255,255,.52);
          box-shadow: inset 0 1px 0 rgba(255,255,255,.60);
        }
        html.dark .fmx-header-glance,
        body.dark .fmx-header-glance,
        .dark .fmx-header-glance,
        [data-theme="dark"] .fmx-header-glance {
          background: rgba(255,255,255,.03);
          box-shadow: none;
        }
        .fmx-header-glance > span {
          min-width: 128px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 2px;
          padding: 9px 12px;
          border-left: 1px solid var(--line);
        }
        .fmx-header-glance > span:first-child { border-left: 0; }
        .fmx-header-glance b {
          color: var(--text);
          font-size: 11px;
          font-weight: 850;
          line-height: 1.15;
        }
        .fmx-header-glance .is-strong b { color: var(--green); }
        .fmx-header-glance .is-weak b { color: var(--red); }
        .fmx-header-glance small {
          color: var(--muted);
          font-size: 8px;
          font-weight: 680;
          white-space: nowrap;
        }
        .fmx-header-console-clean {
          min-width: 310px;
          align-items: flex-end;
          justify-content: center;
          gap: 10px;
          padding-left: 0;
        }
        .fmx-header-update {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 6px;
          color: var(--muted);
          font-size: 9px;
          font-weight: 650;
        }
        .fmx-header-update svg { width: 12px; height: 12px; color: var(--purple); }
        .fmx-header-update strong { color: var(--text); font-weight: 820; }

        /* Macro robuste : aucun objet backend n'est rendu directement par React */
        .fmx-macro-banks { grid-column: span 2; }
        .fmx-bank-list {
          display: grid;
          grid-template-columns: repeat(2,minmax(0,1fr));
          gap: 9px;
        }
        .fmx-bank-list article {
          min-width: 0;
          display: grid;
          grid-template-columns: minmax(180px,.85fr) minmax(150px,.7fr) minmax(230px,1.2fr);
          gap: 14px;
          align-items: center;
          padding: 13px 14px;
          border: 1px solid var(--line);
          border-radius: 15px;
          background: var(--soft);
          box-shadow: inset 0 1px 0 rgba(255,255,255,.52);
        }
        .fmx-bank-list article:first-child { border-top: 1px solid var(--line); }
        html.dark .fmx-bank-list article,
        body.dark .fmx-bank-list article,
        .dark .fmx-bank-list article,
        [data-theme="dark"] .fmx-bank-list article {
          box-shadow: inset 0 1px 0 rgba(255,255,255,.025);
        }
        .fmx-bank-identity {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }
        .fmx-bank-flag {
          width: 40px;
          height: 40px;
          display: grid;
          place-items: center;
          flex: 0 0 40px;
          border: 1px solid var(--line);
          border-radius: 12px;
          background: var(--card-solid);
          font-size: 21px;
          box-shadow: var(--shadow-sm);
        }
        .fmx-bank-identity strong {
          font-size: 13px;
          font-weight: 860;
          letter-spacing: -.015em;
        }
        .fmx-bank-identity small {
          display: block;
          margin-top: 2px;
          color: var(--muted);
          font-size: 9px;
          line-height: 1.35;
        }
        .fmx-bank-policy {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 5px;
        }
        .fmx-policy-pill {
          display: inline-flex;
          align-items: center;
          min-height: 25px;
          padding: 0 9px;
          border-radius: 999px;
          font-size: 9px;
          font-weight: 850;
          white-space: nowrap;
        }
        .fmx-policy-pill.is-hawkish { color: var(--green); background: var(--green-soft); }
        .fmx-policy-pill.is-dovish { color: var(--red); background: var(--red-soft); }
        .fmx-policy-pill.is-neutral { color: var(--sub); background: var(--soft-2); }
        .fmx-bank-policy small {
          color: var(--muted);
          font-size: 8px;
          line-height: 1.35;
        }
        .fmx-bank-next {
          min-width: 0;
          padding-left: 13px;
          border-left: 1px solid var(--line);
        }
        .fmx-bank-next small {
          display: block;
          color: var(--muted);
          font-size: 8px;
          font-weight: 800;
          letter-spacing: .045em;
          text-transform: uppercase;
        }
        .fmx-bank-next strong {
          display: block;
          margin-top: 4px;
          color: var(--text);
          font-size: 10px;
          font-weight: 790;
          line-height: 1.35;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .fmx-bank-next span {
          display: block;
          margin-top: 3px;
          color: var(--muted);
          font-size: 8px;
        }
        .fmx-rate-grid article {
          position: relative;
          padding: 13px;
          border-radius: 14px;
          overflow: hidden;
        }
        .fmx-rate-grid article::after {
          content: "";
          position: absolute;
          inset: auto 10px 0;
          height: 2px;
          border-radius: 999px;
          background: linear-gradient(90deg, var(--purple), transparent);
          opacity: .55;
        }
        .fmx-rate-grid article > small {
          font-size: 8px;
          text-transform: uppercase;
          letter-spacing: .06em;
        }
        .fmx-rate-grid article > strong {
          margin-top: 7px;
          font-size: 18px;
          letter-spacing: -.025em;
        }
        .fmx-rate-grid article > span {
          display: block;
          margin-top: 2px;
          color: var(--purple);
          font-size: 9px;
          font-weight: 850;
        }
        .fmx-curve-reading {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-top: 10px;
          padding: 11px 12px;
          border: 1px solid var(--line);
          border-radius: 13px;
          background: var(--soft);
        }
        .fmx-curve-reading small {
          display: block;
          color: var(--muted);
          font-size: 8px;
          text-transform: uppercase;
          letter-spacing: .055em;
        }
        .fmx-curve-reading strong {
          display: block;
          margin-top: 3px;
          font-size: 11px;
          font-weight: 810;
        }
        .fmx-curve-reading > span {
          color: var(--purple);
          font-size: 13px;
          font-weight: 850;
        }
        .fmx-geo-line > div > small {
          display: block;
          margin-bottom: 3px;
          color: var(--muted);
          font-size: 8px;
          font-weight: 800;
          letter-spacing: .05em;
          text-transform: uppercase;
        }

        @media(max-width:1180px) {
          .fmx-topbar-clean { grid-template-columns: 1fr; gap: 18px; }
          .fmx-header-console-clean { min-width: 0; align-items: stretch; }
          .fmx-header-console-clean .fmx-actions { justify-content: flex-start; }
          .fmx-header-update { justify-content: flex-start; }
          .fmx-bank-list { grid-template-columns: 1fr; }
          .fmx-macro-banks { grid-column: auto; }
        }
        @media(max-width:760px) {
          .fmx-topbar-clean { padding: 18px; }
          .fmx-title-icon-clean {
            width: 48px;
            height: 48px;
            flex-basis: 48px;
            border-radius: 15px;
          }
          .fmx-topbar-clean .fmx-title-wrap { gap: 13px; }
          .fmx-topbar-clean .fmx-title-wrap h1 { font-size: 27px; }
          .fmx-header-glance {
            width: 100%;
            display: grid;
            grid-template-columns: 1fr 1fr;
          }
          .fmx-header-glance > span {
            min-width: 0;
            border-left: 0;
            border-top: 1px solid var(--line);
          }
          .fmx-header-glance > span:nth-child(-n+2) { border-top: 0; }
          .fmx-header-glance > span:nth-child(even) { border-left: 1px solid var(--line); }
          .fmx-bank-list article { grid-template-columns: 1fr; gap: 10px; }
          .fmx-bank-next {
            padding-left: 0;
            padding-top: 10px;
            border-left: 0;
            border-top: 1px solid var(--line);
          }
        }

      `}</style>



      <div className="fmx-page">

        <div className="fmx-shell">

          <header className="fmx-topbar fmx-topbar-premium fmx-topbar-clean">
            <div className="fmx-header-main">
              <div className="fmx-title-wrap">
                <span className="fmx-title-icon fmx-title-icon-clean">
                  <BarChart3 />
                </span>

                <div className="fmx-title-copy">
                  <div className="fmx-header-kicker fmx-header-kicker-clean">
                    <span className="fmx-brand-word">FULLMETRIX</span>
                    <span className="fmx-brand-separator" />
                    <span className="fmx-module-word">Fundamental Intelligence</span>
                  </div>

                  <h1>Analyse fondamentale</h1>

                  <p>
                    Une lecture claire du marché FX à partir des publications macro, des banques centrales,
                    du positionnement et du contexte de risque.
                  </p>

                  <div className="fmx-header-glance">
                    <span className="is-strong">
                      <b><FlagIcon code={headerStrongest?.currency} /> {headerStrongest?.currency || "—"}</b>
                      <small>devise la plus soutenue</small>
                    </span>
                    <span className="is-weak">
                      <b><FlagIcon code={headerWeakest?.currency} /> {headerWeakest?.currency || "—"}</b>
                      <small>devise la plus sous pression</small>
                    </span>
                    <span>
                      <b>{headerSpread.toFixed(1)} pts</b>
                      <small>écart de force maximal</small>
                    </span>
                    <span>
                      <b>{Math.round(data.data_health?.coverage_score || 0)}%</b>
                      <small>couverture des données</small>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="fmx-header-console fmx-header-console-clean">
              <div className="fmx-actions">
                <label className="fmx-zone" aria-label="Fuseau horaire">
                  <Globe2 />
                  <span>Fuseau</span>
                  <select value={timeZone} onChange={(event) => setTimeZone(event.target.value)}>
                    {timeZoneOptions.map((zone) => (
                      <option key={zone} value={zone}>{zone}</option>
                    ))}
                  </select>
                  <ChevronDown />
                </label>

                <button
                  type="button"
                  className="fmx-refresh"
                  onClick={() => loadDashboard({ silent: false })}
                  disabled={refreshing}
                >
                  <RefreshCw className={refreshing ? "fmx-spin" : ""} />
                  <span>{refreshing ? "Actualisation" : "Actualiser"}</span>
                </button>
              </div>

              <div className="fmx-header-update">
                <Clock3 />
                <span>Dernier calcul</span>
                <strong>{formatAge(data.updated_at)}</strong>
              </div>
            </div>
          </header>


          {error ? <div className="fmx-banner"><AlertTriangle /><span><strong>{error}</strong> — le cache reste affiché pour ne pas vider la page.</span></div> : null}



          <nav className="fmx-tabs">

            {[['overview',Eye,'Vue générale'],['pairs',Target,'Paires'],['currencies',BarChart3,'Devises'],['calendar',CalendarDays,'Calendrier'],['regimes',Layers3,'Régimes'],['banks',Landmark,'Banques centrales'],['positioning',BarChart3,'Positionnement'],['risks',ShieldAlert,'Risques'],['playbooks',Sparkles,'Scénarios'],['macro',Activity,'Macro']].map(([key,Icon,label]) => (

              <button key={key} type="button" className={view === key ? "is-active" : ""} onClick={() => setView(key)}><Icon />{label}</button>

            ))}

          </nav>



          {loading ? <div className="fmx-loader">Chargement des données fondamentales…</div> : null}



          {!loading && view === 'overview' ? <DashboardView data={data} timeZone={timeZone} onCurrency={openCurrency} onMarket={openMarket} onEvent={openEvent} /> : null}

          {!loading && view === 'pairs' ? <PairsView data={data} onMarket={openMarket} /> : null}

          {!loading && view === 'currencies' ? <CurrenciesView data={data} onCurrency={openCurrency} /> : null}

          {!loading && view === 'calendar' ? <CalendarView data={data} timeZone={timeZone} onEvent={openEvent} /> : null}

          {!loading && view === 'regimes' ? <RegimesView data={data} onCurrency={openCurrency} /> : null}

          {!loading && view === 'banks' ? <CentralBanksView data={data} timeZone={timeZone} /> : null}

          {!loading && view === 'positioning' ? <PositioningView data={data} /> : null}

          {!loading && view === 'risks' ? <RisksView data={data} timeZone={timeZone} onEvent={openEvent} /> : null}

          {!loading && view === 'playbooks' ? <PlaybooksView data={data} onMarket={openMarket} /> : null}

          {!loading && view === 'macro' ? <MacroView data={data} timeZone={timeZone} /> : null}

        </div>

      </div>



      <Modal
        open={Boolean(selectedMarket)}
        onClose={closeModal}
        eyebrow="Lecture de paire"
        title={selectedMarket?.symbol || "Paire"}
        subtitle={selectedMarket ? `${selectedMarket.base || "—"} vs ${selectedMarket.quote || "—"} · Horizon ${selectedMarket.horizon || "court terme"}` : ""}
      >
        {selectedMarket ? (() => {
          const meta = directionMeta(selectedMarket.direction);
          const DirectionIcon = meta.icon;
          const playbook = selectedMarket.playbook || {};
          const explanation = selectedMarket.explanation || {};
          const story = selectedMarket.trader_story || playbook.trader_story || {};
          const baseSnapshot = selectedMarket.base_snapshot || {};
          const quoteSnapshot = selectedMarket.quote_snapshot || {};
          const decisionText = explanation.decision_meaning || (selectedMarket.decision === "buy_watch" ? "Biais acheteur à surveiller, pas un ordre." : selectedMarket.decision === "sell_watch" ? "Biais vendeur à surveiller, pas un ordre." : "Contexte à confirmer avant toute décision.");
          return <>
            <div className={`fmx-modal-hero is-${meta.key}`}>
              <div className="fmx-modal-hero-icon"><DirectionIcon /></div>
              <div className="fmx-modal-hero-copy">
                <span>Lecture actuelle</span>
                <h4>{selectedMarket.bias || meta.label}</h4>
                <p>{story.narrative || explanation.why_direction || selectedMarket.thesis || "Aucune thèse suffisamment documentée."}</p>
              </div>
              <span className={`fmx-decision-pill is-${meta.key}`}>{decisionText}</span>
            </div>

            <div className="fmx-modal-metrics">
              <MetricCard label="Différentiel" value={`${signedNumber(selectedMarket.differential)} pts`} detail="Base moins cotation" tone={meta.key} />
              <MetricCard label="Confiance scénario" value={`${Math.round(selectedMarket.confidence || 0)}%`} detail="Force de la lecture de paire" />
              <MetricCard label="Qualité données" value={`${Math.round(selectedMarket.data_confidence || 0)}%`} detail={explanation.data_quality || `${selectedMarket.signal_count || 0} facteurs analysés`} />
              <MetricCard label="Décision moteur" value={selectedMarket.bias || meta.label} detail={selectedMarket.horizon || "Intraday / swing court"} tone={meta.key} />
            </div>

            <section className="fmx-pair-compare">
              <article>
                <span className="fmx-compare-kicker">Devise de base</span>
                <div className="fmx-compare-score"><strong><FlagIcon code={baseSnapshot.currency || selectedMarket.base} />{baseSnapshot.currency || selectedMarket.base}</strong><b>{Number(baseSnapshot.score ?? selectedMarket.base_score ?? 50).toFixed(1)}</b></div>
                <small>{baseSnapshot.status || "—"} · confiance {Math.round(baseSnapshot.confidence || 0)}%</small>
                <p>{baseSnapshot.summary || "Lecture détaillée indisponible."}</p>
              </article>
              <div className="fmx-vs-badge">VS</div>
              <article>
                <span className="fmx-compare-kicker">Devise de cotation</span>
                <div className="fmx-compare-score"><strong><FlagIcon code={quoteSnapshot.currency || selectedMarket.quote} />{quoteSnapshot.currency || selectedMarket.quote}</strong><b>{Number(quoteSnapshot.score ?? selectedMarket.quote_score ?? 50).toFixed(1)}</b></div>
                <small>{quoteSnapshot.status || "—"} · confiance {Math.round(quoteSnapshot.confidence || 0)}%</small>
                <p>{quoteSnapshot.summary || "Lecture détaillée indisponible."}</p>
              </article>
            </section>

            <div className="fmx-insight-grid">
              <InsightBlock icon={Target} eyebrow="Scénario principal" title="Pourquoi cette paire ressort maintenant" text={story.main_case || explanation.gap_meaning || selectedMarket.thesis} tone={meta.key} />
              <InsightBlock icon={CheckCircle2} eyebrow="Confirmation" title="Ce qui doit rester vrai" text={story.what_confirms || explanation.confirmation_needed || selectedMarket.trader_action || "Attendre une confirmation technique hors fenêtre de news."} tone="info" />
              <InsightBlock icon={ShieldAlert} eyebrow="Scénario contraire" title="Ce qui peut retourner l'analyse" text={story.alternative_case || story.what_breaks || selectedMarket.invalidation || "Une réaction opposée du prix ou une nouvelle information macro peut invalider le scénario."} tone="warning" />
              <InsightBlock icon={Clock3} eyebrow="Timing" title="Risque événementiel" text={story.timing || "Vérifier les publications des deux devises avant toute exécution."} />
            </div>

            {(playbook.alignments?.length || playbook.conflicts?.length || playbook.event_risks_24h?.length) ? (
              <section className="fmx-playbook-panel">
                <div className="fmx-playbook-head"><div><span>Playbook croisé</span><h4>Confluences et risques qui différencient réellement cette paire</h4></div><b>{playbook.setup || "À confirmer"}</b></div>
                <div className="fmx-playbook-columns">
                  <div>
                    <h5><CheckCircle2 /> Alignements</h5>
                    {(playbook.alignments || []).length ? <ul>{playbook.alignments.map((item, index) => <li key={index}>{item}</li>)}</ul> : <p>Aucun alignement supplémentaire confirmé.</p>}
                  </div>
                  <div>
                    <h5><AlertTriangle /> Conflits / risques</h5>
                    {(playbook.conflicts || []).length ? <ul>{playbook.conflicts.map((item, index) => <li key={index}>{item}</li>)}</ul> : <p>Aucun conflit majeur détecté dans le playbook.</p>}
                  </div>
                </div>
                {(playbook.event_risks_24h || []).length ? <div className="fmx-news-risk-strip">{playbook.event_risks_24h.map((risk, index) => <span key={`${risk.event}-${index}`}><Bell /><strong><FlagIcon code={risk.currency} />{risk.currency}</strong>{risk.event}<small>{risk.countdown}</small></span>)}</div> : null}
              </section>
            ) : null}
          </>;
        })() : null}
      </Modal>

      <Modal
        open={Boolean(selectedCurrency)}
        onClose={closeModal}
        eyebrow="Currency Intelligence"
        title={selectedCurrency ? `${selectedCurrency.currency} · ${currencyMeta(selectedCurrency).name}` : "Devise"}
        subtitle={selectedCurrency ? `${currencyMeta(selectedCurrency).name} · ${currencyMeta(selectedCurrency).bank}` : "Analyse de force relative"}
      >
        {selectedCurrency ? (() => {
          const tone = scoreTone(selectedCurrency.score);
          const meta = currencyMeta(selectedCurrency);
          const CurrencyIcon = tone === "up" ? TrendingUp : tone === "down" ? TrendingDown : Activity;
          const explanation = selectedCurrency.explanation || {};
          const story = selectedCurrency.analysis_story || {};
          const components = selectedCurrency.score_components || {};
          const breakdown = selectedCurrency.breakdown || {};
          const drivers = selectedCurrency.driver_details || [];
          const publishedSignals = explanation.coverage?.published_signals ?? selectedCurrency.evidence_count ?? 0;
          const expectations = explanation.coverage?.expectations ?? 0;
          const pillars = explanation.coverage?.pillars ?? selectedCurrency.pillar_coverage ?? 0;
          const positiveDrivers = explanation.positive_driver_count ?? drivers.filter((item) => Number(item.points || 0) > 0).length;
          const negativeDrivers = explanation.negative_driver_count ?? drivers.filter((item) => Number(item.points || 0) < 0).length;

          return <>
            <section className={`fmx-currency-modal-hero is-${tone}`}>
              <div className="fmx-currency-modal-flag" aria-hidden="true">
                <span><FlagIcon code={selectedCurrency.currency} size="hero" /></span>
                <small>{meta.bankShort}</small>
              </div>

              <div className="fmx-currency-modal-copy">
                <span className="fmx-currency-modal-overline">{meta.region} · {meta.bank}</span>
                <h4>{selectedCurrency.currency}<em>{meta.name}</em></h4>
                <p>{story.one_liner || explanation.headline || selectedCurrency.plain_reading || selectedCurrency.summary}</p>
                <div className="fmx-currency-modal-tags">
                  <span className={`is-${tone}`}><CurrencyIcon /> {currencyStrengthLabel(selectedCurrency.score)}</span>
                  <span><ShieldCheck /> {selectedCurrency.score_status || "Lecture en cours"}</span>
                  <span><Gauge /> {Math.round(selectedCurrency.score_confidence || 0)}% confiance données</span>
                </div>
              </div>

              <div
                className={`fmx-currency-modal-score is-${tone}`}
                style={{ "--score-angle": `${clamp(selectedCurrency.score || 0) * 3.6}deg` }}
              >
                <div>
                  <strong>{Number(selectedCurrency.score || 0).toFixed(1)}</strong>
                  <small>/100</small>
                </div>
                <span>Force relative</span>
              </div>
            </section>

            <section className="fmx-currency-components">
              <article className={Number(components.macro_published || 0) > 0 ? "is-up" : Number(components.macro_published || 0) < 0 ? "is-down" : ""}>
                <div><Database /><span>Macro publié</span></div>
                <strong>{signedNumber(components.macro_published)}</strong>
                <p>Poids issu des résultats déjà publiés. C'est le bloc le plus important du score.</p>
              </article>
              <article className={Number(components.market_expectations || 0) > 0 ? "is-up" : Number(components.market_expectations || 0) < 0 ? "is-down" : ""}>
                <div><Clock3 /><span>Anticipations</span></div>
                <strong>{signedNumber(components.market_expectations)}</strong>
                <p>Consensus et attentes avant publication, avec un poids volontairement inférieur aux faits publiés.</p>
              </article>
              <article className={Number(components.geopolitics || 0) > 0 ? "is-up" : Number(components.geopolitics || 0) < 0 ? "is-down" : ""}>
                <div><Globe2 /><span>Géopolitique</span></div>
                <strong>{signedNumber(components.geopolitics)}</strong>
                <p>Ajustement de contexte limité : il complète le macro mais ne doit pas décider seul du biais.</p>
              </article>
            </section>

            <div className="fmx-modal-metrics fmx-currency-modal-metrics">
              <MetricCard label="Statut du score" value={selectedCurrency.score_status || "—"} detail={`${publishedSignals} publication(s) confirmée(s)`} tone={tone} />
              <MetricCard label="Confiance données" value={`${Math.round(selectedCurrency.score_confidence || 0)}%`} detail={`${pillars} pilier(s) macro couvert(s)`} />
              <MetricCard label="Forces / pressions" value={`${positiveDrivers} / ${negativeDrivers}`} detail="Facteurs positifs vs facteurs négatifs" />
              <MetricCard label="Écart à l'équilibre" value={`${signedNumber(Number(selectedCurrency.score || 0) - 50)} pts`} detail="Distance par rapport à la base neutre 50" tone={tone} />
            </div>

            <div className="fmx-insight-grid">
              <InsightBlock
                icon={Eye}
                eyebrow="Lecture desk"
                title={story.headline || "Ce que l'on observe maintenant"}
                text={story.one_liner || selectedCurrency.plain_reading || selectedCurrency.summary}
                tone={tone}
              />
              <InsightBlock
                icon={Sparkles}
                eyebrow="Moteur dominant"
                title="Ce qui explique vraiment le classement"
                text={story.dominant_driver || explanation.score_meaning || `Le score ${selectedCurrency.score}/100 synthétise les facteurs disponibles autour d'une base neutre à 50.`}
              />
              <InsightBlock
                icon={Target}
                eyebrow="Application trading"
                title="Comment utiliser cette devise dans une paire"
                text={story.trader_read || explanation.direction_meaning || "Comparer cette devise à une devise clairement opposée dans le classement."}
                tone="info"
              />
              <InsightBlock
                icon={ShieldAlert}
                eyebrow="Invalidation"
                title="Ce qui peut changer rapidement la lecture"
                text={(story.invalidation || [])[0] || explanation.confidence_meaning || "Réévaluer la lecture après toute publication importante ou réaction de prix opposée."}
                tone="warning"
              />
            </div>

            {(story.supporting_evidence?.length || story.opposing_evidence?.length) ? (
              <section className="fmx-playbook-panel">
                <div className="fmx-playbook-head">
                  <div>
                    <span>Balance fondamentale</span>
                    <h4>Pourquoi {selectedCurrency.currency} n'a pas la même lecture que les autres devises</h4>
                  </div>
                  <b>{story.conviction || "À confirmer"} · {story.macro_regime || "Régime mixte"}</b>
                </div>
                <div className="fmx-playbook-columns">
                  <div>
                    <h5><TrendingUp /> Ce qui soutient {selectedCurrency.currency}</h5>
                    {(story.supporting_evidence || []).length ? <ul>{story.supporting_evidence.map((item, index) => <li key={index}>{item}</li>)}</ul> : <p>Aucun soutien suffisamment discriminant.</p>}
                  </div>
                  <div>
                    <h5><TrendingDown /> Ce qui pèse ou limite le biais</h5>
                    {(story.opposing_evidence || []).length ? <ul>{story.opposing_evidence.map((item, index) => <li key={index}>{item}</li>)}</ul> : <p>Aucune pression suffisamment discriminante.</p>}
                  </div>
                </div>
                <div className="fmx-news-risk-strip">
                  <span><Landmark /><strong>Banque centrale</strong>{story.policy_context || "—"}</span>
                  <span><BarChart3 /><strong>Régime</strong>{story.macro_regime || "—"}</span>
                  <span><Layers3 /><strong>Positionnement</strong>{story.positioning_context || "—"}</span>
                </div>
              </section>
            ) : null}

            {Object.keys(breakdown).length ? (
              <section className="fmx-breakdown-panel fmx-currency-breakdown">
                <div className="fmx-subsection-title">
                  <div><span>Carte fondamentale</span><h4>D'où vient le score de {selectedCurrency.currency}</h4></div>
                  <p>50 représente une lecture équilibrée pour chaque pilier. Plus haut = soutien relatif ; plus bas = pression relative.</p>
                </div>
                <div className="fmx-breakdown-grid">
                  {Object.entries(breakdown).map(([label, value]) => {
                    const valueTone = scoreTone(value);
                    return (
                      <article className={`is-${valueTone}`} key={label}>
                        <div><span>{label}</span><strong>{Math.round(Number(value || 0))}</strong></div>
                        <div className="fmx-breakdown-track"><i style={{ width: `${clamp(value || 0)}%` }} /><b /></div>
                        <small>{currencyStrengthLabel(value)}</small>
                      </article>
                    );
                  })}
                </div>
              </section>
            ) : null}

            <section className="fmx-driver-section">
              <div className="fmx-subsection-title">
                <div><span>Preuves utilisées</span><h4>Facteurs qui influencent réellement {selectedCurrency.currency}</h4></div>
                <p>Le moteur affiche le type de preuve, son sens et son poids. Une anticipation n'est jamais présentée comme un résultat publié.</p>
              </div>
              {drivers.length ? <div className="fmx-driver-list">{drivers.slice(0, 8).map((item, index) => (
                <article className={Number(item.points || 0) > 0 ? "is-up" : Number(item.points || 0) < 0 ? "is-down" : "is-neutral"} key={`${item.event || "driver"}-${index}`}>
                  <div className="fmx-driver-top">
                    <span>{signalTypeLabel(item.type)}</span>
                    <b className={Number(item.points || 0) > 0 ? "is-up" : Number(item.points || 0) < 0 ? "is-down" : "is-neutral"}>{signedNumber(item.points, 2)} pt</b>
                  </div>
                  <h5>{item.event || `Facteur ${index + 1}`}</h5>
                  <p>{item.explanation || item.reading || "Facteur pris en compte dans le score."}</p>
                  <div className="fmx-driver-foot">
                    {item.pillar_label ? <small>{item.pillar_label}</small> : <span />}
                    <em>{Number(item.points || 0) > 0 ? "Soutient la devise" : Number(item.points || 0) < 0 ? "Pèse sur la devise" : "Effet neutre"}</em>
                  </div>
                </article>
              ))}</div> : <EmptyState title="Pas de facteur détaillé" text="Le moteur n'a pas encore assez d'éléments discriminants pour cette devise." />}
            </section>

            <section className="fmx-watch-panel fmx-watch-panel-premium">
              <div>
                <Bell />
                <span>
                  <small>À surveiller ensuite</small>
                  <strong>Ce qui peut modifier la lecture de {selectedCurrency.currency}</strong>
                </span>
              </div>
              <div className="fmx-watch-chips">
                {(story.next_catalysts?.length ? story.next_catalysts.map((item) => `${item.event} · ${item.countdown}`) : (selectedCurrency.what_to_watch || [])).map((item, index) => <span key={index}><Clock3 /> {item}</span>)}
              </div>
            </section>
          </>;
        })() : null}
      </Modal>

      <Modal
        open={Boolean(selectedEvent)}
        onClose={closeModal}
        eyebrow="Publication macro"
        title={selectedEvent?.event || "Événement"}
        subtitle={selectedEvent ? `${selectedEvent.currency} · ${formatDateTime(selectedEvent.datetime, timeZone)} · ${selectedEvent.category || "Macroéconomie"}`.trim() : ""}
      >
        {selectedEvent ? (() => {
          const state = eventStateMeta(selectedEvent);
          const StateIcon = state.icon;
          const details = selectedEvent.data_explanation || {};
          const comparison = details.comparison || {};
          const marketReading = selectedEvent.market_reading || {};
          const actualVsForecast = comparison.actual_vs_forecast || {};
          const actualVsPrevious = comparison.actual_vs_previous || {};
          const published = Boolean(selectedEvent.published || selectedEvent.actual != null);
          return <>
            <div className={`fmx-modal-hero is-${state.key}`}>
              <div className="fmx-modal-hero-icon"><StateIcon /></div>
              <div className="fmx-modal-hero-copy">
                <span>{state.label}</span>
                <h4>{details.headline || selectedEvent.interpretation_text || "Lecture de la publication"}</h4>
                <p>{marketReading.surprise ? `${marketReading.surprise} ${marketReading.trend || ""}` : comparison.summary || selectedEvent.trader_explanation || "La lecture dépend du résultat, du consensus et de la réaction du prix."}</p>
              </div>
              <span className={`fmx-decision-pill is-${state.key}`}>{tradeStatusLabel(selectedEvent.trade_status)}</span>
            </div>

            <div className="fmx-modal-metrics">
              <MetricCard label="Impact" value={impactLabel(selectedEvent.impact)} detail={selectedEvent.category || "Macroéconomie"} tone={impactLabel(selectedEvent.impact) === "Fort" ? "warning" : "neutral"} />
              <MetricCard label="Confiance lecture" value={`${Math.round(selectedEvent.confidence || 0)}%`} detail={published ? "Résultat publié" : "Avant publication"} />
              <MetricCard label="Statut trader" value={tradeStatusLabel(selectedEvent.trade_status)} detail={selectedEvent.trade_status_text || "Surveiller le timing"} tone={selectedEvent.trade_status === "blocked" ? "warning" : "neutral"} />
              <MetricCard label="Devise" value={selectedEvent.currency} detail={selectedEvent.currency_name || currencyMeta(selectedEvent.currency).name || "Devise concernée"} />
            </div>

            <section className="fmx-event-values">
              {[details.previous || { label: "Précédent", value: selectedEvent.previous }, details.forecast || { label: "Prévision", value: selectedEvent.forecast }, details.actual || { label: "Résultat réel", value: selectedEvent.actual ?? "À venir" }].map((item, index) => (
                <article key={`${item.label}-${index}`} className={index === 2 ? `is-${state.key}` : ""}>
                  <span>{item.label}</span>
                  <strong>{item.value ?? "—"}</strong>
                  <p>{item.explanation || (index === 0 ? "Dernière valeur publiée." : index === 1 ? "Consensus avant publication." : published ? "Valeur effectivement publiée." : "Pas encore publié.")}</p>
                </article>
              ))}
            </section>

            <div className="fmx-insight-grid">
              <InsightBlock icon={Database} eyebrow="Observation" title="Les chiffres utilisés" text={marketReading.observed || `${details.why_it_matters || selectedEvent.what_it_means}`} />
              <InsightBlock icon={Activity} eyebrow="Surprise" title={published ? "Ce que le marché n'attendait pas" : "Ce que le marché attend"} text={marketReading.surprise || selectedEvent.interpretation_text || comparison.summary} tone={state.key} />
              <InsightBlock icon={TrendingUp} eyebrow="Tendance" title={marketReading.alignment === "aligned" ? "Surprise et tendance alignées" : marketReading.alignment === "conflicted" ? "Signal contradictoire" : "Comparaison au précédent"} text={marketReading.trend || actualVsPrevious.explanation || "Aucune comparaison exploitable au précédent."} tone={marketReading.alignment === "conflicted" ? "warning" : state.key} />
              <InsightBlock icon={Landmark} eyebrow="Moteur économique" title={marketReading.focus || selectedEvent.category || "Macroéconomie"} text={details.why_it_matters || selectedEvent.what_it_means} />
            </div>

            {published ? (
              <section className="fmx-event-conclusion">
                <div className="fmx-subsection-title"><div><span>Après publication</span><h4>Interprétation trader sans surinterpréter le chiffre</h4></div></div>
                <div className="fmx-event-conclusion-grid">
                  <InsightBlock icon={Target} eyebrow="Implication FX" title="Ce que le résultat change pour la devise" text={marketReading.implication || selectedEvent.trader_explanation} tone={state.key} />
                  <InsightBlock icon={Eye} eyebrow="Confirmation suivante" title="Ce que le trader doit vérifier maintenant" text={marketReading.watch_next || details.market_mechanism} />
                  <InsightBlock icon={ShieldAlert} eyebrow="Invalidation" title="Quand il faut abandonner cette lecture" text={marketReading.invalidation || details.limitations || "Révisions, sous-composantes, positionnement et réaction du prix peuvent modifier la lecture."} tone="warning" />
                </div>
              </section>
            ) : (
              <section className="fmx-scenario-panel">
                <div className="fmx-subsection-title"><div><span>Avant publication</span><h4>Deux scénarios possibles — aucun n'est encore confirmé</h4></div><p>Le consensus est une attente, pas un résultat.</p></div>
                <div className="fmx-scenario-grid">
                  <InsightBlock icon={TrendingUp} eyebrow="Si le chiffre sort au-dessus" title="Scénario supérieur aux attentes" text={selectedEvent.higher_scenario} tone="up" />
                  <InsightBlock icon={TrendingDown} eyebrow="Si le chiffre sort en dessous" title="Scénario inférieur aux attentes" text={selectedEvent.lower_scenario} tone="down" />
                </div>
              </section>
            )}

            <section className="fmx-event-checklist">
              <div className="fmx-subsection-title"><div><span>Méthode de vérification</span><h4>Comment confirmer la lecture avant d'agir</h4></div></div>
              <div className="fmx-checklist-grid">{(selectedEvent.analysis_steps || selectedEvent.trader_checklist || []).slice(0, 4).map((item, index) => <span key={index}><CheckCircle2 />{item}</span>)}</div>
              {(selectedEvent.affected_assets || []).length ? <div className="fmx-affected-assets"><small>Actifs concernés</small>{selectedEvent.affected_assets.slice(0, 8).map((item) => <span key={item}>{item}</span>)}</div> : null}
            </section>
          </>;
        })() : null}
      </Modal>

    </>

  );

}
